/* Surisa — catalog table interactions.

   Tres custom elements + un techhero carousel:

   1. <surisa-catalog-table> — motor de estado del catálogo. Mantiene
      { sortKey, sortDir, unit, page, pageSize, filters } y reconstruye el
      tbody desde el dataset JSON inline en cada cambio. Pipeline en
      setState: filter → sort → paginate. Pagination 100% cliente
      (sin `{%- paginate -%}` server-side). Mobile usa el modo "Cargar más"
      acumulativo separado del paginado desktop.

   2. <surisa-sort-popover> — popover del toolbar con la lista de columnas
      ordenables + asc/desc. Sincronizado con catalog-table via events.

   3. <surisa-catalog-filters> — wrapper sidebar desktop + drawer mobile
      (mismo DOM, layout responsive por CSS). v0.8.0. Maneja facets de
      tipo checkbox (con counts dinámicos) y range (slider 2-thumb).
      Dispatcha `surisa-catalog-filters:change` con el state.filters
      derivado del DOM en cada interacción (immediate-apply).

   4. <surisa-techhero-carousel> — switch entre las 1-5 imágenes del bloque
      educativo (escala a N: lee todas las .techhero-image del DOM). Sin
      animaciones complejas, solo hidden/visible y estado
      visual del thumb activo.

   Contrato JSON del dataset (consumido por <surisa-catalog-table>):
     {
       columns: [{
         key, label, sortable, unit_base,
         decimals_mm, decimals_in, source_metafield,
         value_prefix,      // v0.12 — prepended al render (ej. "M" → "M3")
         value_suffix,      // v0.12 — appended al render
         unit_display,      // v0.12 — override del literal [unit] del header
         numeric_parser     // v0.12 — 'default' | 'imperial_fractional'
       }],
       rows: [{
         id, url, handle, ref, image,
         cells: {
           // Superset emit por el SSR Liquid — cada fila lleva TODAS las
           // cells de TODAS las familias actuales. La tabla solo lee las
           // cells correspondientes a las columns declaradas en su
           // table_config. Cells no aplicables a la familia van como null.
           erp_code, de_mm, de_in, di_mm, di_in, t_mm, t_in, lo_mm, lo_in,
           ho_mm, ho_in, t_reduced_mm, t_reduced_in,
           force_75_n, force_75_lb, force_100_n, force_100_lb,
           bolt_size, bolt_size_imperial,
           torque_unlubricated_n_m, torque_unlubricated_ft_lbs,
           material, bearing_surface, price, price_formatted
         },
         moq, inventory_quantity, stock_status
       }],
       filters: [{
         key, type ('checkbox' | 'range'),
         source_metafield, label_locale_key, label,
         min, max, step  (range only — null para checkbox)
       }]
     }

   Liquid->JS gotcha (docs/CLAUDE.md): el dataset viaja como
   <script type="application/json">. El parser HTML decodifica las
   entidades al asignar textContent, así que JSON.parse ve raw `<`
   sin necesidad del helper decodeEntities. */

(function () {
  'use strict';

  /* ── helpers ─────────────────────────────────────────────────────── */

  /** Motor de unidades (toggle Métrico ↔ Imperial) — vive en
   *  `assets/surisa-units.js` (`window.SurisaUnits`), compartido con el PDP
   *  (`<surisa-spec-table>`). Se carga ANTES que este script (ambos `defer`,
   *  orden preservado). Aliasamos a nombres locales para no tocar los
   *  call-sites downstream (resolveCellKey / parseImperialFractional / etc.).
   *
   *  Convención: `col.key` SIEMPRE incluye el sufijo métrico canónico
   *  (`_mm`, `_n`, `_n_m`, `_g`); el swap a imperial lo resuelve resolveCellKey
   *  vía SUFFIX_PAIRS (longest-first). Columnas `unitless` pasan sin cambio.
   *  `state.unit` sigue siendo 'mm'/'in' (= SISTEMA) por compat con ?unit=. */
  var Units = window.SurisaUnits || {};
  var SUFFIX_PAIRS            = Units.SUFFIX_PAIRS;
  var TOGGLE_UNIT_BASES       = Units.TOGGLE_UNIT_BASES;
  var UNIT_DISPLAY            = Units.UNIT_DISPLAY;
  var resolveCellKey          = Units.resolveCellKey;
  var parseImperialFractional = Units.parseImperialFractional;

  /** Range filter unit conversion · Opción B (decision recorded in
   *  docs/ARCHITECTURE_collections_catalogo.md § "Toggle mm↔in y filtros").
   *
   *  state.filters[key] always holds canonical mm values regardless of the
   *  active unit toggle. UI display + slider thumbs convert to inches when
   *  state.unit === 'in'. Conversion is purely display-layer: getFilteredRows
   *  always compares against the row's `_mm` cell.
   *
   *  Why this instead of duplicating filter_def records per unit in Admin:
   *  preserves the active filter across toggle changes (25–140 mm active +
   *  toggle to in → thumbs show 0.98–5.51 in, same canonical range), no
   *  Admin duplication, and URL params stay canonical (?filter.de_mm=25:140
   *  works regardless of toggle state). */
  var MM_TO_IN = 1 / 25.4;
  var IN_TO_MM = 25.4;

  function convertMmToIn(mm)  { return Number(mm) * MM_TO_IN; }
  function convertInToMm(inc) { return Number(inc) * IN_TO_MM; }

  /** True if the filter key follows the `<dim>_mm` convention. The Opción B
   *  conversion only applies to these dimensional keys; checkboxes and
   *  non-dimensional ranges (e.g. force_75_n which is N, not mm) pass through. */
  function isDimensionalMmKey(key) {
    return typeof key === 'string' && /_mm$/.test(key);
  }

  // parseImperialFractional vive ahora en surisa-units.js (aliasado arriba a
  // `parseImperialFractional`). Da orden numérico a bolt_size_imperial en
  // getSortedRows ("1-3/8\"" 1.375 < "1-3/4\"" 1.75) en vez de lexicográfico.

  /** Format a numeric slider value for display.
   *  mm → integer (always — step is 1).
   *  in → 2 decimals (step 0.1 visible, 2 decimals readable). */
  function formatRangeValue(v, unit) {
    if (v == null || v === '') return '';
    var n = Number(v);
    if (isNaN(n)) return String(v);
    if (unit === 'in') return n.toFixed(2);
    return Number.isInteger(n) ? String(n) : n.toFixed(1);
  }

  /** Entero con separador de miles es-ES (punto). Espejo determinista del
   *  snippet Liquid `format-thousands-es` (no usamos toLocaleString para no
   *  depender de los datos de locale/ICU del navegador; así SSR y JS dan el
   *  MISMO string). 800 → "800", 1100 → "1.100", 12345 → "12.345". */
  function formatThousandsEs(n) {
    return String(Math.round(Number(n))).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  }

  /** Flatten any shape that a Shopify metafield can take into a flat array
   *  of plain string values. Covers:
   *    - null / "" → []
   *    - "foo"     → ["foo"]
   *    - ["a","b"] → ["a","b"]                  (list.single_line_text)
   *    - [{value:"a"}, {value:"b"}]             (some choice list shapes)
   *    - {value:"a"} / {values:["a","b"]}       (Shopify wrapped value object)
   *    - {handle:"x", label:"X"}                (taxonomy reference shape)
   *  Strings get trimmed; empties dropped. Anything we can't make sense of
   *  is silently skipped (no console noise). */
  function extractChoiceValues(v) {
    if (v == null) return [];
    if (typeof v === 'string') {
      var s = v.trim();
      return s ? [s] : [];
    }
    if (typeof v === 'number') return [String(v)];
    if (Array.isArray(v)) {
      var out = [];
      for (var i = 0; i < v.length; i++) {
        var inner = extractChoiceValues(v[i]);
        for (var j = 0; j < inner.length; j++) out.push(inner[j]);
      }
      return out;
    }
    if (typeof v === 'object') {
      if (v.value !== undefined) return extractChoiceValues(v.value);
      if (v.values !== undefined) return extractChoiceValues(v.values);
      if (v.label !== undefined) return extractChoiceValues(v.label);
      if (v.handle !== undefined) return extractChoiceValues(v.handle);
    }
    return [];
  }

  /* Columnas cuya celda de fila de PRODUCTO es un agregado sobre variantes →
     qué celda numérica de la variante las representa al ordenar. Sólo se
     consulta en modo product_expandable. */
  /* Columnas cuya celda de fila de PRODUCTO es un rango agregado sobre las
     variantes → el PAR de celdas numéricas que lo forman. `min` es además la
     celda con la que se ordena la columna.

     Tienen que ser dos claves distintas: con una sola, el extremo alto salía
     como el máximo de los MÍNIMOS, y un producto cuyas variantes cubren
     14–31 / 32–49 / 10–40 / 25–100 se anunciaba como "10 – 32 ºC" en vez de
     "10 – 100 ºC". El SSR (catalog-rows-expandable.liquid) siempre usó el par
     correcto, así que era además una divergencia entre los dos renders.

     Solo se consulta en modo product_expandable. */
  var AGG_RANGE_CELLS = {
    temp_range_display: { min: 'temp_min_c', max: 'temp_max_c' }
  };

  /* Claves cuyo render de celda es idéntico para una variante y para un
     producto de una sola variante, y por tanto se delegan a fillCell con un
     pseudo-row sintetizado desde la variante (ver variantAsRow). Mantener a
     mano evita duplicar ~200 líneas de precio/stock/cantidad/acciones. */
  var VARIANT_DELEGATED_KEYS = {
    erp_code: true, price: true, stock: true, quantity: true, actions: true
  };

  /* "Oculta esta celda cuando repite esa otra": los puntos exactos de un
     producto de un solo nivel valen lo mismo que su rango ("29 ºC" en ambos),
     y repetirlos no aporta nada. La regla es por dato, no por num_puntos.
     Espeja el `when 'puntos_temperatura'` de catalog-cell-variant.liquid. */
  var DEDUPE_AGAINST = { puntos_temperatura: 'temp_range_display' };

  /** Compact page list: [1, '…', 4, 5, 6, '…', 10] o secuencia plana. */
  function computePages(current, total) {
    if (total <= 1) return [1];
    var window = 1;
    var keep = new Set([1, total, current]);
    for (var i = current - window; i <= current + window; i++) {
      if (i >= 1 && i <= total) keep.add(i);
    }
    var sorted = Array.from(keep).sort(function (a, b) { return a - b; });
    var out = [];
    var prev = 0;
    sorted.forEach(function (p) {
      if (p - prev > 1) out.push('…');
      out.push(p);
      prev = p;
    });
    return out;
  }

  /* ── <surisa-catalog-table> ──────────────────────────────────────── */

  if (!customElements.get('surisa-catalog-table')) {
    customElements.define(
      'surisa-catalog-table',
      class SurisaCatalogTable extends HTMLElement {
        constructor() {
          super();
          this.catalogData = null;
          this.sectionId = '';
          this.state = {
            sortKey: null,
            sortDir: 'asc',
            unit: 'mm',
            page: 1,
            pageSize: 12,
            filters: {}
          };
          // ── Plegado en modo product_expandable ─────────────────────────
          // Dos conjuntos en vez de uno para que el toggle sea de verdad
          // independiente por producto y para que un colapso manual gane
          // sobre el auto-expand del filtro. Sin `collapsedIds`, filtrar
          // dejaría todos los coincidentes abiertos y el usuario no podría
          // cerrar ninguno. Se vacían al cambiar de filtros, que es lo que
          // devuelve la tabla a su estado inicial (primero abierto).
          this.expandedIds  = Object.create(null);
          this.collapsedIds = Object.create(null);
          this.expandable = false;
          this.variantLevelKeys = Object.create(null);
        }

        connectedCallback() {
          this.sectionId = this.getAttribute('data-section-id') || '';

          // Granularidad de fila (v0.13) — la emite el SSR desde
          // `table_config.row_granularity`. Cualquier valor que no sea
          // exactamente 'product_expandable' (incluido el atributo ausente)
          // deja el componente en el modo histórico: una fila por producto.
          // Ver el bloque de cabecera de page-catalogo.liquid.
          this.expandable = (this.getAttribute('data-row-granularity') || 'product')
            .trim() === 'product_expandable';

          var script = this.querySelector('script[data-catalog-dataset]');
          if (!script) return;
          try {
            this.catalogData = JSON.parse(script.textContent);
          } catch (err) {
            console.error('[surisa-catalog-table] dataset parse failed', err);
            return;
          }

          this.tbody = this.querySelector('tbody');
          this.thead = this.querySelector('thead');

          // Initial state from data-* attrs + SSR row count.
          var defaultSortKey = (this.getAttribute('data-default-sort-key') || '').trim();
          var defaultSortDir = (this.getAttribute('data-default-sort-direction') || 'asc').trim();
          this.state.sortKey = defaultSortKey || null;
          this.state.sortDir = defaultSortDir === 'desc' ? 'desc' : 'asc';
          // El pageSize se deduce de cuántas filas pintó el SSR. En modo
          // expandable el tbody trae también las filas hijas, así que hay que
          // contar sólo las de producto: son ellas la unidad de paginación.
          var ssrCount = 0;
          if (this.tbody) {
            ssrCount = this.expandable
              ? this.tbody.querySelectorAll('tr[data-product-row]').length
              : this.tbody.children.length;
          }
          this.state.pageSize = ssrCount > 0 ? ssrCount : 12;
          if (this.expandable) this.computeVariantLevelKeys();
          // Snapshot the SSR-default so writeUrlParams can keep the URL
          // clean when the user stays on the default. URL `?per_page=N`
          // overrides happen AFTER (via readUrlParams).
          this.defaultPageSize = this.state.pageSize;

          // URL params override the metaobject defaults — lets users share
          // links with a specific sort/unit/page applied.
          Object.assign(this.state, this.readUrlParams());
          // Clamp page within bounds.
          var pageCount = Math.max(1, Math.ceil(this.catalogData.rows.length / this.state.pageSize));
          if (this.state.page > pageCount) this.state.page = pageCount;
          if (this.state.page < 1) this.state.page = 1;

          // External UI references (counter + pagination + mobile load-more).
          var root = this.parentElement || document;
          this.countShownEl       = root.querySelector('[data-count-shown]');
          this.countOfTotalEl     = root.querySelector('[data-count-of-total]');
          this.paginationEl       = root.querySelector('[data-pagination-nav]');
          this.paginationShownEl  = root.querySelector('[data-pagination-shown]');
          this.paginationCtrlsEl  = root.querySelector('[data-pagination-controls]');
          this.loadMoreEl         = root.querySelector('[data-mobile-load-more]');
          this.loadMoreBtn        = root.querySelector('[data-load-more-btn]');
          this.loadMoreShownEl    = root.querySelector('[data-load-more-shown]');
          this.loadMoreTotalEl    = root.querySelector('[data-load-more-total]');
          this.loadMoreBarEl      = root.querySelector('[data-load-more-bar]');
          // Mobile results counter inside the trigger button (badge updates).
          this.mobileResultsEl    = root.querySelector('[data-mobile-results]');

          this.mobileLoadIncrement = parseInt(this.getAttribute('data-mobile-load-more') || '12', 10);
          this.currentMobileShown  = ssrCount;

          // i18n templates (rendered server-side with placeholder tokens
          // we substitute at runtime — see formatCount + formatShowing).
          // Note: the chip strip was removed in v0.8.0 refactor V4 (pills
          // horizontal en la filters-bar son los chips), so chip-remove-aria
          // y clear-all locale templates ya no se consumen aquí.
          this.i18nCountOne       = this.getAttribute('data-i18n-count-one')       || '';
          this.i18nCountOther     = this.getAttribute('data-i18n-count-other')     || '';
          this.i18nShowing        = this.getAttribute('data-i18n-showing')         || '';
          this.i18nOfTotal        = this.getAttribute('data-i18n-of-total')        || '';

          // v0.9.0 — i18n + config para columnas Stock/Cantidad/Acciones.
          // Todos los strings con placeholder __REF__/__QTY__/__N__ los
          // sustituye el JS en runtime al pintar la celda o al cambiar el
          // input de cantidad. El `consultaUrl` es la URL raw del schema
          // setting; puede contener __REF__/__QTY__ para mailto dinámico.
          this.consultaUrl                = this.getAttribute('data-consulta-url')                || '';
          this.i18nStockIn                = this.getAttribute('data-i18n-stock-in')                || '';
          this.i18nStockLow               = this.getAttribute('data-i18n-stock-low')               || '';
          this.i18nStockOut               = this.getAttribute('data-i18n-stock-out')               || '';
          this.i18nMoqCaptionOne          = this.getAttribute('data-i18n-moq-caption-one')         || '';
          this.i18nMoqCaptionOther        = this.getAttribute('data-i18n-moq-caption-other')       || '';
          this.i18nAddLabel               = this.getAttribute('data-i18n-add-label')               || '';
          this.i18nAddDisabledTooltip     = this.getAttribute('data-i18n-add-disabled-tooltip')    || '';
          this.i18nAddAria                = this.getAttribute('data-i18n-add-aria')                || '';
          this.i18nConsultarLabel         = this.getAttribute('data-i18n-consultar-label')         || '';
          this.i18nConsultarAria          = this.getAttribute('data-i18n-consultar-aria')          || '';
          this.i18nQtyInputAria           = this.getAttribute('data-i18n-qty-input-aria')          || '';
          this.i18nQtyDecAria             = this.getAttribute('data-i18n-qty-dec-aria')            || '';
          this.i18nQtyIncAria             = this.getAttribute('data-i18n-qty-inc-aria')            || '';
          this.i18nPriceConsultar         = this.getAttribute('data-i18n-price-consultar')         || '';
          // v0.13 — plantilla del sufijo de precio con __FORMAT__ como token.
          // Vacía ⇒ nunca se pinta sufijo (comportamiento pre-v0.13).
          this.i18nPriceUnitFormat        = this.getAttribute('data-i18n-price-unit-format')       || '';

          // v0.13 — plantillas de la fila de producto en modo expandable.
          // Vacías en los catálogos que no lo activan (el atributo no se
          // emite), y ninguna se consulta fuera de fillProductCell.
          this.i18nPriceFrom              = this.getAttribute('data-i18n-price-from')              || '';
          this.i18nStockOfTotal           = this.getAttribute('data-i18n-stock-of-total')          || '';
          this.i18nVariantsCountOne       = this.getAttribute('data-i18n-variants-count-one')      || '';
          this.i18nVariantsCountOther     = this.getAttribute('data-i18n-variants-count-other')    || '';
          this.i18nVariantsMatchedOne     = this.getAttribute('data-i18n-variants-matched-one')    || '';
          this.i18nVariantsMatchedOther   = this.getAttribute('data-i18n-variants-matched-other')  || '';
          this.i18nVariantsToggleAria     = this.getAttribute('data-i18n-variants-toggle-aria')    || '';
          this.i18nViewProduct            = this.getAttribute('data-i18n-view-product')            || '';
          this.i18nViewProductAria        = this.getAttribute('data-i18n-view-product-aria')       || '';

          // Listeners delegados para el stepper + input de cantidad. Cubren
          // tanto las filas SSR como las hidratadas por paginación cliente.
          this.addEventListener('click', this.handleQtyStepperClick.bind(this));
          this.addEventListener('input', this.handleQtyInputChange.bind(this));
          this.addEventListener('change', this.handleQtyInputBlur.bind(this));

          if (this.loadMoreBtn) {
            this.loadMoreBtn.addEventListener('click', this.handleLoadMore.bind(this));
          }

          // Sortable header bindings — delegated through thead but per-th
          // for easy class toggling on active column.
          this.sortableThs = this.thead
            ? Array.from(this.thead.querySelectorAll('th[data-sortable="true"]'))
            : [];
          var selfHandler = this;
          this.sortableThs.forEach(function (th) {
            th.style.cursor = 'pointer';
            th.addEventListener('click', function () { selfHandler.handleHeaderClick(th); });
          });

          // Unit toggle bindings (mm ↔ in).
          this.unitToggleEl = root.querySelector('[data-unit-toggle]');
          this.unitToggleBtns = this.unitToggleEl
            ? Array.from(this.unitToggleEl.querySelectorAll('[data-unit]'))
            : [];
          this.unitToggleBtns.forEach(function (btn) {
            btn.addEventListener('click', function () {
              var u = btn.getAttribute('data-unit');
              if ((u === 'mm' || u === 'in') && u !== selfHandler.state.unit) {
                selfHandler.setState({ unit: u });
              }
            });
          });

          // Per-page toggle bindings (v0.9.0 — pills 12/24/48/96).
          this.perPageToggleEl = root.querySelector('[data-per-page-toggle]');
          this.perPageBtns = this.perPageToggleEl
            ? Array.from(this.perPageToggleEl.querySelectorAll('[data-per-page]'))
            : [];
          this.perPageBtns.forEach(function (btn) {
            btn.addEventListener('click', function () {
              var v = parseInt(btn.getAttribute('data-per-page'), 10);
              if (!isNaN(v) && v > 0 && v !== selfHandler.state.pageSize) {
                selfHandler.setState({ pageSize: v });
              }
            });
          });

          // If state matches the SSR-rendered natural order (no sort,
          // unit=mm, page=1, no filters), skip the rebuild. Any deviation
          // triggers a rebuild so the tbody syncs with state.
          var hasFilters = this.state.filters && Object.keys(this.state.filters).length > 0;
          var matchesSSR = !this.state.sortKey
            && this.state.unit === 'mm'
            && this.state.page === 1
            && !hasFilters;
          // En modo expandable el rebuild es incondicional: el SSR emite las
          // variantes en el orden de Admin y el JS las reordena por
          // temp_min_c ascendente. Reconstruir ya en el arranque evita que el
          // usuario vea el orden de Admin y luego un salto al primer click.
          if (!matchesSSR || this.expandable) {
            this.renderTbody();
          }
          this.applySortHeaderUI();
          this.applyUnitUI();
          this.applyPerPageUI();
          this.updateCountUI();
          this.updatePaginationUI();
          this.updateMobileLoadMoreUI();
          this.writeUrlParams();

          // Toggle del plegado — delegado, así que cubre las filas del SSR y
          // las que reconstruye el JS sin re-bindear nada.
          if (this.expandable) {
            this.addEventListener('click', this.handleGroupToggleClick.bind(this));
            // Llegada por ?variant=<id> desde una ficha, un email o un enlace
            // compartido: desplegar el producto que la contiene y llevar al
            // usuario hasta ella. Después del primer render para que la fila
            // exista en el DOM.
            this.focusVariantFromUrl();
          }

          this.setupScrollAffordance();

          this.dispatchReady(false);

          // El dataset SSR solo trae la primera página (máx. 250 filas por el
          // tope de `paginate`). Si la colección tiene más, traer el resto en
          // segundo plano y concatenar para que sort/filtros/contadores operen
          // sobre el set COMPLETO. Ver loadRemainingPages.
          this.loadRemainingPages();
        }

        disconnectedCallback() {
          this.catalogData = null;
          if (this.scrollAffordanceCleanup) this.scrollAffordanceCleanup();
        }

        /* ── Afordancia de scroll horizontal (fix móvil sep-2026) ─────
         *  La tabla completa se mantiene por decisión de diseño, así que el
         *  problema no es el scroll sino que no se veía que existiera: los
         *  controles de Cantidad/Comprar viven a ~900px a la derecha y nada
         *  lo anunciaba.
         *
         *  Este método gobierna las dos señales que añade el CSS:
         *    · `data-scroll-end` en el viewport → apaga el degradado del
         *      borde derecho cuando ya no queda nada oculto (o cuando la
         *      tabla cabe entera y el degradado sobra desde el principio).
         *    · `data-scrolled` en el aviso de texto → lo desvanece en cuanto
         *      el usuario desliza por primera vez; cumplida su función, deja
         *      de ocupar atención.
         *
         *  Todo es no-crítico: si los nodos no existen (catálogos servidos
         *  por una versión anterior del Liquid) el método no hace nada. */
        setupScrollAffordance() {
          var scroller = this.querySelector('[data-table-scroll]');
          var viewport = this.querySelector('[data-table-viewport]');
          if (!scroller || !viewport) return;

          var hint = this.querySelector('[data-scroll-hint]');
          var self = this;

          /* El `left` con el que se fija la columna de Referencia tiene que
             ser el ancho REAL de la columna de imagen, no una constante:
             el algoritmo de tabla le reparte el sobrante y ese ancho
             cambia con el número de filas, la familia y el idioma. Se
             mide y se publica como custom property; el CSS la consume
             con el valor histórico como fallback. */
          function syncStickyOffset() {
            var head = self.querySelector('.surisa-catalog__th--image');
            if (!head) return;
            var w = head.getBoundingClientRect().width;
            if (!w) return;
            self.style.setProperty('--sc-sticky-sku-left', w + 'px');
          }

          function syncEdge() {
            // 2px de tolerancia: los navegadores devuelven scrollLeft
            // fraccionario con zoom o densidades no enteras y el degradado
            // se quedaba encendido en el tope derecho.
            var hidden = scroller.scrollWidth - scroller.clientWidth - scroller.scrollLeft;
            viewport.setAttribute('data-scroll-end', hidden <= 2 ? 'true' : 'false');
          }

          function onScroll() {
            syncEdge();
            if (hint && scroller.scrollLeft > 8) {
              hint.setAttribute('data-scrolled', 'true');
            }
          }

          scroller.addEventListener('scroll', onScroll, { passive: true });
          syncEdge();
          syncStickyOffset();

          // El ancho del contenido cambia al filtrar, ordenar, paginar o
          // cuando entra el resto del dataset — todas ellas pueden hacer que
          // la tabla pase a caber (o deje de caber) sin que haya scroll.
          function syncAll() { syncEdge(); syncStickyOffset(); }

          var ro = null;
          if (typeof ResizeObserver !== 'undefined') {
            ro = new ResizeObserver(syncAll);
            ro.observe(scroller);
            var tbl = scroller.querySelector('table');
            if (tbl) ro.observe(tbl);
          } else {
            window.addEventListener('resize', syncAll);
          }

          this.scrollAffordanceCleanup = function () {
            scroller.removeEventListener('scroll', onScroll);
            if (ro) ro.disconnect();
            else window.removeEventListener('resize', syncAll);
            self.scrollAffordanceCleanup = null;
          };
        }

        /* ── Carga del resto de páginas (colecciones > page_size) ─────
         *  Shopify capa `for product in collection.products` a 50 sin
         *  paginate, y a 250 con paginate. Para colecciones mayores el SSR
         *  solo puede emitir la primera página; el resto se piden vía Section
         *  Rendering API (`?section_id=<id>&page=N`), se les extrae el dataset
         *  JSON embebido y se concatenan sus filas. Al terminar se reconstruye
         *  la vista, los contadores y las facetas (datasetExtended). */
        loadRemainingPages() {
          var pg = this.catalogData && this.catalogData.pagination;
          if (!pg || !pg.pages || pg.pages <= 1 || !this.sectionId) return;

          var self = this;
          var basePath = window.location.pathname;
          var reqs = [];
          for (var p = 2; p <= pg.pages; p++) {
            var url = basePath
              + '?section_id=' + encodeURIComponent(this.sectionId)
              + '&page=' + p;
            reqs.push(
              fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
                .then(function (r) { return r.ok ? r.text() : ''; })
                .then(function (html) { return self.extractRowsFromHtml(html); })
                .catch(function () { return []; })
            );
          }

          Promise.all(reqs).then(function (chunks) {
            var added = 0;
            for (var i = 0; i < chunks.length; i++) {
              var rows = chunks[i];
              if (rows && rows.length) {
                self.catalogData.rows = self.catalogData.rows.concat(rows);
                added += rows.length;
              }
            }
            if (added > 0) self.onFullDatasetLoaded();
          });
        }

        /** Extrae el array `rows` del dataset JSON embebido en el HTML que
         *  devuelve la Section Rendering API. Devuelve [] si algo falla
         *  (la tabla sigue funcionando con las filas ya cargadas). */
        extractRowsFromHtml(html) {
          if (!html) return [];
          try {
            var doc = new DOMParser().parseFromString(html, 'text/html');
            var s = doc.querySelector('script[data-catalog-dataset]');
            if (!s) return [];
            var data = JSON.parse(s.textContent);
            return Array.isArray(data.rows) ? data.rows : [];
          } catch (err) {
            console.error('[surisa-catalog-table] page dataset parse failed', err);
            return [];
          }
        }

        /** Tras concatenar todas las páginas: re-clamp de página, re-render,
         *  contadores y re-emisión de `ready` con datasetExtended para que las
         *  facetas de filtro se reconstruyan con el set completo (preservando
         *  la selección del usuario vía syncUiFromState). */
        onFullDatasetLoaded() {
          var pageCount = Math.max(1, Math.ceil(this.catalogData.rows.length / this.state.pageSize));
          if (this.state.page > pageCount) this.state.page = pageCount;
          this.renderTbody();
          this.updateCountUI();
          this.updatePaginationUI();
          this.updateMobileLoadMoreUI();
          this.dispatchReady(true);
        }

        /** Emite `surisa-catalog:ready`. `datasetExtended=true` señala que las
         *  filas crecieron (segunda+ emisión) para que los consumidores
         *  reconstruyan facetas en vez de tratarlo como primer sync. */
        dispatchReady(datasetExtended) {
          document.dispatchEvent(
            new CustomEvent('surisa-catalog:ready', {
              detail: {
                sectionId: this.sectionId,
                columns: this.catalogData.columns,
                rows: this.catalogData.rows,
                filters: this.catalogData.filters || [],
                filteredCount: this.getFilteredRows().length,
                datasetExtended: datasetExtended === true,
                state: Object.assign({}, this.state)
              }
            })
          );
        }

        /* ── State patcher + render orchestrator ──────────────────── */

        setState(patch) {
          // Reset page to 1 on order-affecting changes. pageSize counts too —
          // when the user switches 12 → 96 from page 5, "page 5" no longer
          // refers to the same slice; safer to jump back to 1.
          var resetPage = patch.sortKey !== undefined
            || patch.sortDir !== undefined
            || patch.unit !== undefined
            || patch.filters !== undefined
            || patch.pageSize !== undefined;
          // Reset mobile cursor on filter changes — the accumulated list is
          // built from filtered+sorted rows, so a smaller filtered set needs
          // to restart from the top of the (new) sorted view.
          var resetMobileCursor = patch.filters !== undefined;
          // Cualquier cambio de filtros devuelve el plegado a su estado por
          // defecto. Eso cubre las dos direcciones que pide el diseño:
          // aplicar filtro → los coincidentes se auto-expanden (isExpanded),
          // limpiarlo → vuelve el estado inicial con sólo el primero abierto.
          if (this.expandable && patch.filters !== undefined) {
            this.expandedIds  = Object.create(null);
            this.collapsedIds = Object.create(null);
          }
          Object.assign(this.state, patch);
          if (resetPage && this.state.page !== 1) {
            this.state.page = 1;
          }
          if (resetMobileCursor) {
            this.currentMobileShown = Math.min(this.mobileLoadIncrement, this.getFilteredRows().length);
          }
          this.renderTbody();
          this.applySortHeaderUI();
          this.applyUnitUI();
          this.applyPerPageUI();
          this.updateCountUI();
          this.updatePaginationUI();
          this.updateMobileLoadMoreUI();
          this.writeUrlParams();
          document.dispatchEvent(
            new CustomEvent('surisa-catalog:state-change', {
              detail: {
                sectionId: this.sectionId,
                state: Object.assign({}, this.state),
                filteredCount: this.getFilteredRows().length
              }
            })
          );
        }

        /* ── URL params (shareable links) ─────────────────────────── */

        readUrlParams() {
          var out = {};
          try {
            var params = new URLSearchParams(window.location.search);
            var sort = params.get('sort');
            if (sort && this.catalogData) {
              var col = this.catalogData.columns.find(function (c) {
                return c.key === sort && c.sortable;
              });
              if (col) out.sortKey = sort;
            }
            var dir = params.get('dir');
            if (dir === 'asc' || dir === 'desc') out.sortDir = dir;
            var unit = params.get('unit');
            if (unit === 'mm' || unit === 'in') out.unit = unit;
            var page = parseInt(params.get('page'), 10);
            if (!isNaN(page) && page >= 1) out.page = page;
            // Per-page selector — only accept the 4 schema-supported values
            // (12/24/48/96); silently drop anything else to defend against
            // stale links.
            var pp = parseInt(params.get('per_page'), 10);
            if (!isNaN(pp) && [12, 24, 48, 96].indexOf(pp) !== -1) {
              out.pageSize = pp;
            }

            // Filters: ?filter.<key>=v1,v2  (checkbox) or  min:max  (range).
            // Unknown keys are silently dropped — defends against stale links
            // pointing to filters that no longer exist in `table_config.filters_v2`.
            if (this.catalogData && this.catalogData.filters) {
              var defs = this.catalogData.filters.reduce(function (m, f) { m[f.key] = f; return m; }, {});
              var filters = {};
              params.forEach(function (val, key) {
                if (key.indexOf('filter.') !== 0) return;
                var fkey = key.slice(7);
                var def = defs[fkey];
                if (!def) return;
                if (def.type === 'checkbox') {
                  var arr = val.split(',').filter(function (s) { return s !== ''; });
                  if (arr.length > 0) filters[fkey] = arr;
                } else if (def.type === 'range') {
                  var parts = val.split(':');
                  var mn = parts[0] !== '' ? Number(parts[0]) : null;
                  var mx = parts.length > 1 && parts[1] !== '' ? Number(parts[1]) : null;
                  if (mn != null && !isNaN(mn) || mx != null && !isNaN(mx)) {
                    filters[fkey] = {
                      min: mn != null && !isNaN(mn) ? mn : null,
                      max: mx != null && !isNaN(mx) ? mx : null
                    };
                  }
                }
              });
              if (Object.keys(filters).length > 0) out.filters = filters;
            }
          } catch (e) { /* URLSearchParams not available */ }
          return out;
        }

        writeUrlParams() {
          if (!window.history || !window.history.replaceState) return;
          try {
            var params = new URLSearchParams(window.location.search);
            if (this.state.sortKey) params.set('sort', this.state.sortKey);
            else params.delete('sort');
            if (this.state.sortKey && this.state.sortDir === 'desc') params.set('dir', 'desc');
            else params.delete('dir');
            if (this.state.unit === 'in') params.set('unit', 'in');
            else params.delete('unit');
            if (this.state.page > 1) params.set('page', String(this.state.page));
            else params.delete('page');
            // Per-page in URL only when it diverges from the SSR default
            // (the value Joan picks in section.settings.results_per_page).
            // Keeps shareable links clean for the dominant case.
            if (this.defaultPageSize && this.state.pageSize !== this.defaultPageSize) {
              params.set('per_page', String(this.state.pageSize));
            } else {
              params.delete('per_page');
            }

            // Clear stale filter.* params, then re-emit from current state.
            Array.from(params.keys()).forEach(function (k) {
              if (k.indexOf('filter.') === 0) params.delete(k);
            });
            var filters = this.state.filters || {};
            Object.keys(filters).forEach(function (key) {
              var v = filters[key];
              if (Array.isArray(v) && v.length > 0) {
                params.set('filter.' + key, v.join(','));
              } else if (v && typeof v === 'object' && (v.min != null || v.max != null)) {
                var minStr = v.min != null ? String(v.min) : '';
                var maxStr = v.max != null ? String(v.max) : '';
                params.set('filter.' + key, minStr + ':' + maxStr);
              }
            });

            var qs = params.toString();
            var url = window.location.pathname + (qs ? '?' + qs : '') + window.location.hash;
            window.history.replaceState(window.history.state, '', url);
          } catch (e) { /* swallow — URL update is best-effort */ }
        }

        /* ── Sort UI handlers ─────────────────────────────────────── */

        handleHeaderClick(th) {
          var key = (th.getAttribute('data-col-key') || '').trim();
          if (!key) return;
          var nextDir = 'asc';
          if (this.state.sortKey === key) {
            nextDir = this.state.sortDir === 'asc' ? 'desc' : 'asc';
          }
          this.setState({ sortKey: key, sortDir: nextDir });
        }

        applySortHeaderUI() {
          if (!this.sortableThs) return;
          var activeKey = this.state.sortKey;
          var activeDir = this.state.sortDir;
          this.sortableThs.forEach(function (th) {
            var key = (th.getAttribute('data-col-key') || '').trim();
            var isActive = key === activeKey;
            th.classList.toggle('surisa-catalog__th--active', isActive);
            if (isActive) {
              th.setAttribute('aria-sort', activeDir === 'desc' ? 'descending' : 'ascending');
              th.dataset.sortDirection = activeDir;
              var existing = th.querySelector('.surisa-catalog__th-chevron');
              if (existing) {
                existing.dataset.direction = activeDir;
              } else {
                th.appendChild(makeChevron(activeDir));
              }
            } else {
              th.removeAttribute('aria-sort');
              delete th.dataset.sortDirection;
              var ch = th.querySelector('.surisa-catalog__th-chevron');
              if (ch) ch.remove();
            }
          });
        }

        applyUnitUI() {
          // Swap header suffix per unit_base × system. Generalizado v0.12:
          // hoy aplica a mm/in + n/lb + n_m/ft·lbs + (futuro) g/oz. Solo
          // afecta a columnas cuyo data-unit-base está en TOGGLE_UNIT_BASES;
          // unitless y desconocidos pasan inalterados.
          //
          // Refactor patrón B (v0.12): si col.unit_display está poblado en el
          // column_def, override el lookup hardcoded UNIT_DISPLAY. Útil para
          // columnas con unit_base unitless que aún quieren un literal
          // específico (ej. [Ref], [ud]) o variantes de simbolos no estándar.
          // Nota: el override solo aplica al system 'metric' actualmente — si
          // entra un caso con override per-imperial, exponer 2 campos o un
          // pipe-separated value en column_def.
          if (this.thead) {
            var system = this.state.unit === 'in' ? 'imperial' : 'metric';
            var display = UNIT_DISPLAY[system] || {};
            var cols = (this.catalogData && this.catalogData.columns) || [];
            var ths = this.thead.querySelectorAll('th[data-unit-base]');
            ths.forEach(function (th) {
              var ub = th.getAttribute('data-unit-base');
              if (!TOGGLE_UNIT_BASES[ub]) return;
              var colKey = th.getAttribute('data-col-key');
              var unitEl = th.querySelector('.surisa-catalog__col-unit');
              if (!unitEl) return;
              // Check col.unit_display override (only on metric — see comment)
              var col = cols.find(function (c) { return c.key === colKey; });
              var override = (system === 'metric' && col && col.unit_display) ? col.unit_display : '';
              var label = override || display[ub];
              if (label) unitEl.textContent = '[' + label + ']';
            });
          }
          // Sync the toggle buttons' active state.
          if (this.unitToggleBtns) {
            var activeUnit = this.state.unit;
            this.unitToggleBtns.forEach(function (btn) {
              var u = btn.getAttribute('data-unit');
              var isActive = u === activeUnit;
              btn.classList.toggle('surisa-catalog__unit--active', isActive);
              btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
            });
          }
        }

        applyPerPageUI() {
          if (!this.perPageBtns) return;
          var current = this.state.pageSize;
          this.perPageBtns.forEach(function (btn) {
            var v = parseInt(btn.getAttribute('data-per-page'), 10);
            var isActive = (v === current);
            btn.setAttribute('data-active', isActive ? 'true' : 'false');
            btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
          });
        }

        /* ── Filtered dataset view ────────────────────────────────── */

        /** Apply state.filters to the raw dataset.
         *  Checkbox filter: row.cells[key] must be in the active values array.
         *  Range filter:    row.cells[key] (as Number) must be between min/max
         *                   (inclusive). Rows whose cell value is null/NaN are
         *                   excluded from range filters (consistent with sort,
         *                   which sends null/empty to the end).
         *  Unknown filter keys are skipped (defensive — table_config.filters_v2
         *  could change without a redeploy). */
        getFilteredRows() {
          if (!this.catalogData) return [];
          var rows = this.catalogData.rows;
          var filters = this.state.filters || {};
          var defs = (this.catalogData.filters || []).reduce(function (m, f) {
            m[f.key] = f;
            return m;
          }, {});
          Object.keys(filters).forEach(function (key) {
            var def = defs[key];
            if (!def) return;
            var v = filters[key];
            if (def.type === 'checkbox' && Array.isArray(v) && v.length > 0) {
              rows = rows.filter(function (r) {
                var cv = (r.cells || {})[key];
                // Use extractChoiceValues to handle every metafield shape
                // (string, list, wrapped object). A row matches if ANY of
                // its extracted values is in the active filter set.
                var rowVals = extractChoiceValues(cv);
                for (var i = 0; i < rowVals.length; i++) {
                  if (v.indexOf(rowVals[i]) !== -1) return true;
                }
                return false;
              });
            } else if (def.type === 'range' && v && typeof v === 'object') {
              var mn = v.min, mx = v.max;
              if (mn == null && mx == null) return;
              rows = rows.filter(function (r) {
                var cv = Number((r.cells || {})[key]);
                if (isNaN(cv)) return false;
                if (mn != null && cv < mn) return false;
                if (mx != null && cv > mx) return false;
                return true;
              });
            }
          });
          return this.expandable ? this.annotateMatches(rows) : rows;
        }

        /* ── Modo product_expandable ──────────────────────────────────
         *  Todo lo de este bloque es inerte cuando `this.expandable` es
         *  false, que es el caso de los 6 catálogos de muelles. */

        /** Índice de qué claves de celda viven en la variante y no en el
         *  producto. Se deduce del propio dataset (qué claves traen los
         *  objetos de `row.variants[].cells`) en vez de mantener una lista
         *  paralela que se desincronizaría del Liquid que lo emite. */
        computeVariantLevelKeys() {
          var rows = (this.catalogData && this.catalogData.rows) || [];
          for (var i = 0; i < rows.length; i++) {
            var vs = rows[i].variants || [];
            for (var j = 0; j < vs.length; j++) {
              var cells = vs[j].cells || {};
              for (var k in cells) this.variantLevelKeys[k] = true;
            }
          }
        }

        /** ¿Hay algún filtro activo que discrimine POR VARIANTE? Es lo que
         *  dispara el auto-expand y el recálculo de agregados. Un filtro de
         *  nivel producto (Nº pts, Formato…) selecciona productos enteros y
         *  no toca sus variantes. */
        hasActiveVariantFilter() {
          var filters = this.state.filters || {};
          var keys = Object.keys(filters);
          for (var i = 0; i < keys.length; i++) {
            if (!this.variantLevelKeys[keys[i]]) continue;
            if (this.filterIsActive(filters[keys[i]])) return true;
          }
          return false;
        }

        filterIsActive(v) {
          if (Array.isArray(v)) return v.length > 0;
          if (v && typeof v === 'object') return v.min != null || v.max != null;
          return false;
        }

        /** Anota en cada fila qué variantes pasan los filtros de variante
         *  (`_matched`) y descarta los productos que no conservan ninguna.
         *
         *  El filtrado de PRODUCTOS ya lo hizo getFilteredRows con el motor
         *  de siempre: la celda `temp_points` del producto es la unión de los
         *  puntos de sus variantes, así que "producto con alguna variante
         *  coincidente" sale del código existente sin tocarlo. Aquí sólo se
         *  resuelve el detalle de CUÁLES coinciden, que es lo que necesitan
         *  el auto-expand y los agregados.
         *
         *  Anota in situ (no copia): las filas son objetos propios y
         *  `_matched` se recalcula antes de cada render, así que no puede
         *  quedar obsoleto. */
        annotateMatches(rows) {
          var filters = this.state.filters || {};
          var defs = (this.catalogData.filters || []).reduce(function (m, f) {
            m[f.key] = f;
            return m;
          }, {});
          var activeKeys = Object.keys(filters).filter(function (k) {
            return this.variantLevelKeys[k] && this.filterIsActive(filters[k]);
          }, this);

          var out = [];
          for (var i = 0; i < rows.length; i++) {
            var row = rows[i];
            var variants = row.variants || [];
            if (!activeKeys.length) {
              row._matched = variants;
              row._filtered = false;
              out.push(row);
              continue;
            }
            var matched = [];
            for (var j = 0; j < variants.length; j++) {
              if (this.variantMatches(variants[j], activeKeys, filters, defs)) {
                matched.push(variants[j]);
              }
            }
            row._matched = matched;
            row._filtered = true;
            // Sin ninguna variante coincidente el producto desaparece de la
            // tabla: mostrarlo vacío sería peor que no mostrarlo.
            if (matched.length > 0) out.push(row);
          }
          return out;
        }

        /** Una variante coincide si pasa TODOS los filtros de variante
         *  activos (AND entre facetas, OR dentro de una faceta) — la misma
         *  semántica que aplica getFilteredRows a los productos. */
        variantMatches(variant, activeKeys, filters, defs) {
          var cells = variant.cells || {};
          for (var i = 0; i < activeKeys.length; i++) {
            var key = activeKeys[i];
            var def = defs[key];
            var want = filters[key];
            if (!def) continue;
            if (def.type === 'checkbox') {
              var have = extractChoiceValues(cells[key]);
              var hit = false;
              for (var j = 0; j < have.length; j++) {
                if (want.indexOf(have[j]) !== -1) { hit = true; break; }
              }
              if (!hit) return false;
            } else if (def.type === 'range') {
              var cv = Number(cells[key]);
              if (isNaN(cv)) return false;
              if (want.min != null && cv < want.min) return false;
              if (want.max != null && cv > want.max) return false;
            }
          }
          return true;
        }

        /** Variantes a pintar bajo un producto: las coincidentes cuando hay
         *  filtro de variante, todas cuando no. Ordenadas por temperatura
         *  mínima ascendente; las que no declaran temp_min_c van al final
         *  conservando su orden de Admin. */
        visibleVariants(row) {
          var list = (row._matched || row.variants || []).slice();
          list.sort(function (a, b) {
            var an = Number((a.cells || {}).temp_min_c);
            var bn = Number((b.cells || {}).temp_min_c);
            var aBad = isNaN(an), bBad = isNaN(bn);
            if (aBad && bBad) return 0;
            if (aBad) return 1;
            if (bBad) return -1;
            return an - bn;
          });
          return list;
        }

        /** ¿Debe arrancar desplegado este producto?
         *  Prioridad: colapso explícito → expansión explícita → auto-expand
         *  por filtro → ser el producto por defecto de la primera página. El
         *  colapso explícito va primero para que el usuario pueda cerrar un
         *  producto incluso con el filtro activo. */
        isExpanded(row, isDefaultOpen) {
          var id = String(row.id);
          if (this.collapsedIds[id]) return false;
          if (this.expandedIds[id]) return true;
          if (this.hasActiveVariantFilter()) return true;
          return isDefaultOpen && this.state.page === 1;
        }

        /* ── Sorted dataset view (filter → sort) ──────────────────── */

        getSortedRows() {
          var rows = this.getFilteredRows();
          if (!this.state.sortKey) return rows;
          var col = this.catalogData.columns.find(function (c) {
            return c.key === this.state.sortKey;
          }.bind(this));
          if (!col) return rows;
          var cellKey = resolveCellKey(col, this.state.unit);
          var dir = this.state.sortDir === 'desc' ? -1 : 1;

          // Ordenar por una columna cuyo valor de fila es un AGREGADO. La
          // celda de producto de Temperatura es texto ("29 – 290 ºC"), así
          // que compararla daría un orden lexicográfico sin sentido; se
          // compara el mínimo numérico de sus variantes visibles, que es
          // exactamente el número que el usuario está viendo a la izquierda
          // del rango. AGG_RANGE_CELLS es el punto de extensión si otra familia
          // añade más columnas agregadas.
          var aggPair = this.expandable ? AGG_RANGE_CELLS[col.key] : null;
          var aggCell = aggPair ? aggPair.min : null;
          if (aggCell) {
            var selfAgg = this;
            return rows.slice().sort(function (a, b) {
              var an = selfAgg.aggMin(a, aggCell);
              var bn = selfAgg.aggMin(b, aggCell);
              var aBad = an == null, bBad = bn == null;
              if (aBad && bBad) return 0;
              if (aBad) return 1;    // sin dato, siempre al final
              if (bBad) return -1;
              return dir * (an - bn);
            });
          }
          // Dispatcher numérico v0.12 — col.numeric_parser del column_def
          // selecciona el parser de comparación:
          //   'default'             → Number coerce, fallthrough imperial,
          //                           fallthrough lex (compat con pre-v0.12).
          //   'imperial_fractional' → solo parseImperialFractional, fallthrough
          //                           directo a lex (skip Number coerce que
          //                           podría dar resultados extraños).
          // Vacío equivale a 'default'.
          var parser = col.numeric_parser || 'default';
          return rows.slice().sort(function (a, b) {
            var va = (a.cells || {})[cellKey];
            var vb = (b.cells || {})[cellKey];
            var an = va == null || va === '';
            var bn = vb == null || vb === '';
            if (an && bn) return 0;
            if (an) return 1;   // null/empty always last regardless of dir
            if (bn) return -1;

            if (parser !== 'imperial_fractional') {
              var na = Number(va);
              var nb = Number(vb);
              if (!isNaN(na) && !isNaN(nb)) return dir * (na - nb);
            }
            if (parser === 'imperial_fractional' || parser === 'default') {
              // "1-3/8\"" → 1.375. Both sides must parse for numeric compare.
              var ia = parseImperialFractional(va);
              var ib = parseImperialFractional(vb);
              if (!isNaN(ia) && !isNaN(ib)) return dir * (ia - ib);
            }
            var sa = String(va).toLowerCase();
            var sb = String(vb).toLowerCase();
            if (sa < sb) return -1 * dir;
            if (sa > sb) return  1 * dir;
            return 0;
          });
        }

        getCurrentPageRows() {
          var sorted = this.getSortedRows();
          var start = (this.state.page - 1) * this.state.pageSize;
          return sorted.slice(start, start + this.state.pageSize);
        }

        /* ── DOM rebuild ──────────────────────────────────────────── */

        renderTbody() {
          if (!this.tbody) return;
          // Mobile mode = single accumulating list. Desktop mode = page slice.
          // We detect via the current viewport — but state.page is what drives
          // desktop. For now, render the desktop page slice; the mobile
          // "Cargar más" path runs through handleLoadMore() and appends.
          var rows = this.getCurrentPageRows();
          while (this.tbody.firstChild) this.tbody.removeChild(this.tbody.firstChild);
          var frag = document.createDocumentFragment();
          if (this.expandable) {
            // Arranca desplegado el primer producto de la página: ahora todos
            // son grupos, así que no hay que buscar el primero "con hijas".
            for (var g = 0; g < rows.length; g++) {
              this.appendProductGroup(frag, rows[g], g === 0);
            }
          } else {
            for (var i = 0; i < rows.length; i++) frag.appendChild(this.buildRow(rows[i]));
          }
          this.tbody.appendChild(frag);
          // El cursor de "Cargar más" cuenta unidades de fila, que en modo
          // expandable son productos, no <tr>.
          this.currentMobileShown = rows.length;
        }

        /* ── Grupo producto + variantes (modo expandable) ──────────── */

        appendProductGroup(frag, row, isDefaultOpen) {
          var variants = this.visibleVariants(row);
          // Todo producto es un grupo desplegable, también con una sola
          // variante: la consistencia de la columna de referencia pesa más que
          // el clic ahorrado (decisión del usuario, ago 2026). Espeja
          // catalog-rows-expandable.liquid.
          var expanded = this.isExpanded(row, isDefaultOpen === true);

          frag.appendChild(this.buildProductRow(row, variants, expanded));
          for (var i = 0; i < variants.length; i++) {
            frag.appendChild(this.buildVariantRow(row, variants[i], expanded));
          }
        }

        /** Fila de producto: chevron + celdas comerciales agregadas sobre sus
         *  variantes. Espeja snippets/catalog-rows-expandable.liquid. */
        buildProductRow(row, variants, expanded) {
          var tr = document.createElement('tr');
          tr.className = 'surisa-catalog__row surisa-catalog__row--product';
          tr.setAttribute('data-product-row', '');
          if (row.id != null)  tr.dataset.productId = String(row.id);
          if (row.url)         tr.dataset.productUrl = row.url;
          if (row.handle)      tr.dataset.productHandle = row.handle;
          tr.dataset.groupSize = String(variants.length);
          tr.dataset.expanded = expanded ? 'true' : 'false';

          var self = this;
          var sortKey = this.state.sortKey;
          this.catalogData.columns.forEach(function (col) {
            var td = document.createElement('td');
            td.className = 'surisa-catalog__td surisa-catalog__td--' + col.key;
            td.dataset.colKey = col.key;
            if (col.key === 'image' || col.key === 'erp_code') {
              td.classList.add('surisa-catalog__td--sticky');
            }
            if (col.key === sortKey) td.classList.add('surisa-catalog__td--active');
            self.fillProductCell(td, col, row, variants, expanded);
            tr.appendChild(td);
          });
          return tr;
        }

        buildVariantRow(row, variant, expanded) {
          var tr = document.createElement('tr');
          tr.className = 'surisa-catalog__row surisa-catalog__row--variant';
          tr.id = 'v-' + this.sectionId + '-' + variant.id;
          tr.setAttribute('data-variant-row', '');
          if (row.id != null) tr.dataset.parentProduct = String(row.id);
          if (row.handle)     tr.dataset.productHandle = row.handle;
          tr.dataset.variantId   = String(variant.id);
          tr.dataset.moq         = String(variant.moq != null ? variant.moq : 1);
          tr.dataset.stockStatus = variant.stock_status || 'out_of_stock';
          if (variant.ref) tr.dataset.ref = variant.ref;
          // `hidden`, no display:none por CSS — así el estado plegado es el
          // mismo para el navegador y para el árbol de accesibilidad. La hoja
          // de estilos NO declara `display` para --variant, o ganaría al
          // [hidden] del user-agent (GOTCHAS Frontend §2).
          if (!expanded) tr.hidden = true;

          var self = this;
          var pseudo = this.variantAsRow(row, variant);
          this.catalogData.columns.forEach(function (col) {
            var td = document.createElement('td');
            td.className = 'surisa-catalog__td surisa-catalog__td--' + col.key;
            td.dataset.colKey = col.key;
            if (col.key === 'image' || col.key === 'erp_code') {
              td.classList.add('surisa-catalog__td--sticky');
            }
            if (col.key === 'image') {
              // La foto es del modelo, no de la referencia: no se repite.
            } else if (VARIANT_DELEGATED_KEYS[col.key] || self.variantLevelKeys[col.key]) {
              self.fillVariantCell(td, col, pseudo);
            } else {
              // Columna de nivel producto: su valor se repetiría idéntico en
              // todas las hermanas. Se atenúa en vez de duplicarse; la columna
              // no se oculta porque la cabecera sigue rotulando el dato.
              td.appendChild(self.makeNa());
            }
            tr.appendChild(td);
          });
          return tr;
        }

        /** Pseudo-fila sintetizada desde una variante, para poder reutilizar
         *  fillCell tal cual (precio, stock, cantidad, acciones y el render
         *  por defecto) sin duplicar su lógica. Las celdas de variante pisan
         *  las de producto; el resto se heredan. */
        variantAsRow(row, variant) {
          var cells = {};
          var pc = row.cells || {};
          for (var k in pc) cells[k] = pc[k];
          var vc = variant.cells || {};
          for (var k2 in vc) cells[k2] = vc[k2];
          return {
            id: row.id,
            url: variant.url || row.url,
            handle: row.handle,
            variant_id: variant.id,
            ref: variant.ref,
            moq: variant.moq != null ? variant.moq : row.moq,
            inventory_quantity: variant.inventory_quantity,
            stock_status: variant.stock_status,
            supply_format: variant.supply_format || row.supply_format,
            image: null,
            cells: cells
          };
        }

        makeNa() {
          var span = document.createElement('span');
          span.className = 'surisa-catalog__na';
          span.textContent = '—';
          return span;
        }

        /** Celda de una variante — la usan tanto las filas hijas como las
         *  filas de producto planas, igual que su espejo en Liquid
         *  (catalog-cell-variant.liquid). Todo lo que no tenga regla propia se
         *  delega a fillCell para no duplicar precio/stock/cantidad/acciones.
         *
         *  Las dos reglas propias existen porque el render por defecto de
         *  fillCell asume que la celda es un número desnudo:
         *   · Columnas agregadas (Temperatura): el dato de la variante YA viene
         *     formateado con su unidad ("37 – 46 ºC"). El `value_suffix` del
         *     column_def está ahí para decorar el agregado numérico de la fila
         *     de producto, así que aplicarlo aquí duplicaba el símbolo
         *     ("0 ºCºC") y hacía que el JS no coincidiera con el SSR.
         *   · Columnas que repiten otra (puntos exactos vs rango): se atenúan. */
        fillVariantCell(td, col, pseudo) {
          var cells = pseudo.cells || {};
          if (AGG_RANGE_CELLS[col.key]) {
            var raw = cells[col.key];
            if (raw == null || raw === '') { td.appendChild(this.makeNa()); return; }
            var s = document.createElement('span');
            s.className = 'tnum';
            s.textContent = String(raw);
            td.appendChild(s);
            return;
          }
          var against = DEDUPE_AGAINST[col.key];
          if (against) {
            var v = cells[col.key];
            if (v == null || v === '' || String(v) === String(cells[against])) {
              td.appendChild(this.makeNa());
              return;
            }
            var s2 = document.createElement('span');
            s2.className = 'tnum';
            s2.textContent = String(v);
            td.appendChild(s2);
            return;
          }
          this.fillCell(td, col, pseudo);
        }

        /** Menor valor numérico de `cellKey` entre las variantes visibles de
         *  una fila. null si ninguna lo declara. */
        aggMin(row, cellKey) {
          var list = row._matched || row.variants || [];
          var out = null;
          for (var i = 0; i < list.length; i++) {
            var n = Number((list[i].cells || {})[cellKey]);
            if (isNaN(n)) continue;
            if (out === null || out > n) out = n;
          }
          return out;
        }

        aggMax(row, cellKey) {
          var list = row._matched || row.variants || [];
          var out = null;
          for (var i = 0; i < list.length; i++) {
            var n = Number((list[i].cells || {})[cellKey]);
            if (isNaN(n)) continue;
            if (out === null || n > out) out = n;
          }
          return out;
        }

        /** Celdas de la fila de producto con 2+ variantes. TODOS los valores
         *  comerciales se calculan sobre `row._matched`, es decir sobre las
         *  variantes que pasan el filtro — no sobre las 49 del producto. Con
         *  un filtro de 120 ºC activo, 1-nivel no puede seguir diciendo
         *  "29 – 290 ºC · 49 referencias · desde 21,13 €". */
        fillProductCell(td, col, row, variants, expanded) {
          var cells = row.cells || {};
          switch (col.key) {
            case 'image':
              this.fillCell(td, col, row);
              break;

            case 'erp_code': {
              var name = row.name || row.ref || '';
              var btn = document.createElement('button');
              btn.type = 'button';
              btn.className = 'surisa-catalog__group-toggle';
              btn.setAttribute('data-group-toggle', '');
              btn.setAttribute('aria-expanded', expanded ? 'true' : 'false');
              // aria-controls con la lista de ids de las hijas (separados por
              // espacio, válido en ARIA). El botón es el punto de acceso por
              // teclado; la fila entera es clicable para el ratón.
              var ids = [];
              for (var i = 0; i < variants.length; i++) {
                ids.push('v-' + this.sectionId + '-' + variants[i].id);
              }
              btn.setAttribute('aria-controls', ids.join(' '));
              if (this.i18nVariantsToggleAria) {
                btn.setAttribute('aria-label', this.i18nVariantsToggleAria.replace(/__NAME__/g, name));
              }
              var chev = document.createElement('span');
              chev.className = 'surisa-catalog__group-chevron';
              chev.setAttribute('aria-hidden', 'true');
              chev.innerHTML =
                '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor"'
                + ' stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">'
                + '<path d="M9 6l6 6-6 6"/></svg>';
              var nameEl = document.createElement('span');
              nameEl.className = 'surisa-catalog__group-name';
              nameEl.textContent = name;
              btn.appendChild(chev);
              btn.appendChild(nameEl);
              td.appendChild(btn);
              break;
            }

            case 'price': {
              // El mínimo se recalcula sobre las coincidentes, así que con
              // filtro activo el "desde" ya no es el del catálogo completo.
              // `minSf` es el formato de suministro de la variante que marca el
              // mínimo, no el de la primera ni el del producto: el importe que
              // se pinta es el de esa variante. Espeja el SSR del snippet.
              var min = null, varies = false, minSf = null;
              for (var p = 0; p < variants.length; p++) {
                var n = Number(variants[p].price);
                if (isNaN(n)) continue;
                if (min === null) { min = n; minSf = variants[p].supply_format; }
                else if (min !== n) {
                  varies = true;
                  if (min > n) { min = n; minSf = variants[p].supply_format; }
                }
              }
              var priceSpan = document.createElement('span');
              if (min === null || min === 0) {
                priceSpan.className = 'surisa-catalog__cell-price surisa-catalog__cell-price--consultar';
                priceSpan.textContent = this.i18nPriceConsultar || 'Consultar';
              } else {
                priceSpan.className = 'surisa-catalog__cell-price';
                var money = this.formatMoneyLike(variants, min);
                priceSpan.textContent = (varies && this.i18nPriceFrom)
                  ? this.i18nPriceFrom.replace(/__PRICE__/g, money)
                  : money;
                var sf = minSf || row.supply_format;
                if (sf && this.i18nPriceUnitFormat) {
                  var sfSpan = document.createElement('span');
                  sfSpan.className = 'surisa-catalog__cell-price-unit';
                  sfSpan.textContent = ' ' + this.i18nPriceUnitFormat.replace(/__FORMAT__/g, sf);
                  priceSpan.appendChild(sfSpan);
                }
              }
              td.appendChild(priceSpan);
              break;
            }

            case 'stock': {
              var inStock = 0;
              for (var s = 0; s < variants.length; s++) {
                if (variants[s].stock_status === 'in_stock') inStock++;
              }
              var badge = document.createElement('span');
              badge.className = 'surisa-catalog__stock-badge surisa-catalog__stock-badge--agg';
              badge.dataset.stockStatus = inStock > 0 ? 'in_stock' : 'out_of_stock';
              var dot = document.createElement('span');
              dot.className = 'surisa-catalog__stock-dot';
              dot.setAttribute('aria-hidden', 'true');
              badge.appendChild(dot);
              var txt = (this.i18nStockOfTotal || '__IN__/__TOTAL__')
                .replace(/__IN__/g, formatThousandsEs(inStock))
                .replace(/__TOTAL__/g, formatThousandsEs(variants.length));
              badge.appendChild(document.createTextNode(' ' + txt));
              td.appendChild(badge);
              break;
            }

            case 'quantity': {
              // En la fila de producto no hay nada que añadir al carrito. Con
              // filtro activo la celda dice cuántas de las N originales
              // coinciden, para que el usuario sepa que está viendo un
              // subconjunto y de qué tamaño.
              var span = document.createElement('span');
              span.className = 'surisa-catalog__group-refs';
              span.setAttribute('data-group-refs', '');
              var total = row.variant_count != null
                ? Number(row.variant_count)
                : (row.variants || []).length;
              var shown = variants.length;
              if (row._filtered && shown !== total) {
                var tpl = shown === 1 ? this.i18nVariantsMatchedOne : this.i18nVariantsMatchedOther;
                span.textContent = (tpl || '__MATCHED__/__TOTAL__')
                  .replace(/__MATCHED__/g, formatThousandsEs(shown))
                  .replace(/__TOTAL__/g, formatThousandsEs(total));
                span.classList.add('surisa-catalog__group-refs--filtered');
              } else {
                var tplC = shown === 1 ? this.i18nVariantsCountOne : this.i18nVariantsCountOther;
                span.textContent = (tplC || String(shown))
                  .replace(/__N__/g, formatThousandsEs(shown));
              }
              td.appendChild(span);
              break;
            }

            case 'actions': {
              var wrap = document.createElement('div');
              wrap.className = 'surisa-catalog__actions';
              var a = document.createElement('a');
              a.className = 'surisa-catalog__btn surisa-catalog__btn--consultar surisa-catalog__btn--outline';
              a.href = row.url || '#';
              if (this.i18nViewProductAria) {
                a.setAttribute('aria-label', this.i18nViewProductAria.replace(/__NAME__/g, row.name || ''));
              }
              a.innerHTML =
                '<svg class="surisa-catalog__btn-icon" aria-hidden="true" viewBox="0 0 20 20" width="14" height="14">'
                + '<path d="M3 4h11v12H3z M6 7h5M6 10h5M6 13h3" fill="none" stroke="currentColor"'
                + ' stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
              a.appendChild(document.createTextNode(this.i18nViewProduct || ''));
              wrap.appendChild(a);
              td.appendChild(wrap);
              break;
            }

            default: {
              if (AGG_RANGE_CELLS[col.key]) {
                // Columna agregada (Temperatura): mín–máx sobre las variantes
                // visibles, con los decimales y el símbolo de unidad del
                // column_def. Espeja el render SSR del snippet.
                var pair = AGG_RANGE_CELLS[col.key];
                var lo = this.aggMin(row, pair.min);
                var hi = this.aggMax(row, pair.max);
                if (lo === null) { td.appendChild(this.makeNa()); break; }
                var dec = col.decimals_mm != null ? col.decimals_mm : 0;
                var suf = col.value_suffix ? ' ' + col.value_suffix : '';
                var loS = lo.toFixed(dec), hiS = hi.toFixed(dec);
                var span2 = document.createElement('span');
                span2.className = 'tnum';
                span2.textContent = (loS === hiS ? loS : loS + ' – ' + hiS) + suf;
                td.appendChild(span2);
              } else if (this.variantLevelKeys[col.key]) {
                // Dato por variante que no se puede agregar de forma legible
                // (los puntos exactos de 1-nivel serían 49 valores).
                td.appendChild(this.makeNa());
              } else {
                this.fillCell(td, col, row);
              }
            }
          }
        }

        /** Formatea `min` con el mismo formato de moneda que trae el dataset,
         *  reutilizando el `price_formatted` de la variante cuyo precio es el
         *  mínimo. Así no hay que replicar el filtro `money` de Liquid (ni su
         *  configuración de divisa) en JS. */
        formatMoneyLike(variants, min) {
          for (var i = 0; i < variants.length; i++) {
            if (Number(variants[i].price) === min && variants[i].price_formatted) {
              return variants[i].price_formatted;
            }
          }
          return String(min);
        }

        /* ── Toggle del plegado ────────────────────────────────────── */

        handleGroupToggleClick(e) {
          if (!e.target || !e.target.closest) return;
          var tr = e.target.closest('tr[data-product-row]');
          if (!tr || !this.contains(tr)) return;
          if (tr.dataset.expanded === undefined) return;   // no es un grupo

          // La fila entera es clicable, pero sin secuestrar los enlaces y
          // botones que contiene (la foto enlaza a la ficha, "Ver ficha"
          // también). El propio chevron sí es un botón y debe togglear.
          if (!e.target.closest('[data-group-toggle]')) {
            if (e.target.closest('a, button, input, select, label')) return;
          }
          this.toggleGroup(tr);
        }

        /** Toggle independiente por producto: no cierra ningún otro. Comparar
         *  varios modelos a la vez es un caso de uso real, así que nada de
         *  acordeón ni exclusividad mutua. */
        toggleGroup(tr) {
          var id = tr.dataset.productId;
          if (!id) return;
          var open = tr.dataset.expanded !== 'true';
          tr.dataset.expanded = open ? 'true' : 'false';
          if (open) {
            this.expandedIds[id] = true;
            delete this.collapsedIds[id];
          } else {
            this.collapsedIds[id] = true;
            delete this.expandedIds[id];
          }
          var btn = tr.querySelector('[data-group-toggle]');
          if (btn) btn.setAttribute('aria-expanded', open ? 'true' : 'false');

          // Las hijas son las hermanas siguientes hasta la próxima fila de
          // producto. Recorrer el DOM en vez de consultar el dataset mantiene
          // el toggle correcto también sobre las filas que pintó el SSR.
          var node = tr.nextElementSibling;
          while (node && !node.hasAttribute('data-product-row')) {
            if (node.hasAttribute('data-variant-row')) node.hidden = !open;
            node = node.nextElementSibling;
          }
        }

        /* ── Llegada por ?variant= ─────────────────────────────────── */

        /** Un enlace con ?variant=<id> apunta a una referencia concreta:
         *  desplegar su producto, traerlo a la página correcta, hacer scroll
         *  y resaltarla. Sin esto el usuario aterriza en una tabla de 14
         *  filas plegadas sin saber cuál le interesa. */
        focusVariantFromUrl() {
          var wanted;
          try {
            wanted = new URLSearchParams(window.location.search).get('variant');
          } catch (e) { return; }
          if (!wanted) return;

          var rows = this.getSortedRows();
          var target = null, idx = -1;
          for (var i = 0; i < rows.length && !target; i++) {
            var vs = rows[i].variants || [];
            for (var j = 0; j < vs.length; j++) {
              if (String(vs[j].id) === String(wanted)) { target = rows[i]; idx = i; break; }
            }
          }
          if (!target) return;

          this.expandedIds[String(target.id)] = true;
          delete this.collapsedIds[String(target.id)];

          // La fila puede caer en otra página de la paginación cliente.
          var page = Math.floor(idx / this.state.pageSize) + 1;
          if (page !== this.state.page) {
            this.state.page = page;
            this.updatePaginationUI();
          }
          this.renderTbody();

          var el = this.querySelector('#v-' + cssEscape(this.sectionId) + '-' + cssEscape(String(wanted)));
          if (!el) return;
          el.classList.add('is-target');
          if (el.scrollIntoView) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }

        updateCountUI() {
          var total = this.getFilteredRows().length;
          // Total real del catálogo = pagination.total (all_products_count del
          // server). Usarlo en vez de rows.length evita que el "de N
          // referencias" parpadee (250 → 793) mientras se cargan en segundo
          // plano las páginas restantes de una colección grande.
          var grand = (this.catalogData.pagination && this.catalogData.pagination.total)
            || this.catalogData.rows.length;
          // Always emit "N resultados" in the bold span; the of-total span
          // stays as " · de M referencias en DIN 2093" (M = grand total) so
          // the user knows the catalog dimension even after filtering.
          if (this.countShownEl) {
            this.countShownEl.textContent = this.formatCount(total);
          }
          if (this.countOfTotalEl && this.i18nOfTotal) {
            this.countOfTotalEl.textContent = ' · ' + this.i18nOfTotal.replace('__TOTAL__', String(grand));
          }
          // Mirror onto the mobile filters trigger button (results badge).
          if (this.mobileResultsEl) {
            this.mobileResultsEl.textContent = this.formatCount(total);
          }
        }

        formatCount(n) {
          if (n === 1 && this.i18nCountOne) return this.i18nCountOne;
          if (this.i18nCountOther) return this.i18nCountOther.replace('__N__', String(n));
          return String(n);
        }

        formatShowing(from, to, total) {
          if (this.i18nShowing) {
            return this.i18nShowing
              .replace('__FROM__', String(from))
              .replace('__TO__', String(to))
              .replace('__TOTAL__', String(total));
          }
          return from + '–' + to + ' / ' + total;
        }

        updatePaginationUI() {
          if (!this.paginationEl || !this.paginationCtrlsEl) return;
          var total = this.getFilteredRows().length;

          // No products at all → hide the entire footer (shown + per-page +
          // controls). Avoids rendering "0 de 0 referencias" alongside a
          // per-page selector that the user has nothing to apply it to.
          if (total === 0) {
            this.paginationEl.hidden = true;
            return;
          }
          this.paginationEl.hidden = false;

          var pageCount = Math.max(1, Math.ceil(total / this.state.pageSize));
          var start = (this.state.page - 1) * this.state.pageSize + 1;
          var end = Math.min(start + this.state.pageSize - 1, total);
          if (this.paginationShownEl) {
            this.paginationShownEl.textContent = this.formatShowing(start, end, total);
          }

          var ctrls = this.paginationCtrlsEl;
          while (ctrls.firstChild) ctrls.removeChild(ctrls.firstChild);

          // Single-page case → hide the page-number nav but keep shown +
          // per-page selector visible (the user may still want to drop the
          // per-page to surface a future filter that produces more rows).
          if (pageCount <= 1) {
            ctrls.hidden = true;
            return;
          }
          ctrls.hidden = false;

          var self = this;
          var current = this.state.page;

          ctrls.appendChild(makeNavLink('‹', current === 1, function () {
            self.setState({ page: current - 1 });
          }, 'surisa-catalog__pagination-link--prev'));

          var pages = computePages(current, pageCount);
          pages.forEach(function (p) {
            if (p === '…') {
              var span = document.createElement('span');
              span.className = 'surisa-catalog__pagination-link surisa-catalog__pagination-link--ellipsis';
              span.textContent = '…';
              ctrls.appendChild(span);
              return;
            }
            if (p === current) {
              var cur = document.createElement('span');
              cur.className = 'surisa-catalog__pagination-link surisa-catalog__pagination-link--current';
              cur.setAttribute('aria-current', 'page');
              cur.textContent = String(p);
              ctrls.appendChild(cur);
              return;
            }
            ctrls.appendChild(makeNavLink(String(p), false, function () {
              self.setState({ page: p });
            }));
          });

          ctrls.appendChild(makeNavLink('›', current === pageCount, function () {
            self.setState({ page: current + 1 });
          }, 'surisa-catalog__pagination-link--next'));
        }

        updateMobileLoadMoreUI() {
          if (!this.loadMoreEl) return;
          var total = this.getFilteredRows().length;
          if (this.loadMoreShownEl) this.loadMoreShownEl.textContent = String(this.currentMobileShown);
          if (this.loadMoreTotalEl) this.loadMoreTotalEl.textContent = String(total);
          if (this.loadMoreBarEl) {
            var pct = total > 0 ? Math.min(100, (this.currentMobileShown / total) * 100) : 100;
            this.loadMoreBarEl.style.width = pct + '%';
          }
          if (this.loadMoreBtn) {
            var exhausted = this.currentMobileShown >= total;
            this.loadMoreBtn.disabled = exhausted;
            this.loadMoreBtn.setAttribute('aria-disabled', exhausted ? 'true' : 'false');
            this.loadMoreBtn.style.display = exhausted ? 'none' : '';
          }
          this.loadMoreEl.setAttribute('aria-valuenow', String(this.currentMobileShown));
        }

        handleLoadMore() {
          if (!this.catalogData || !this.tbody) return;
          var sorted = this.getSortedRows();
          var total = sorted.length;
          var start = this.currentMobileShown;
          var end = Math.min(start + this.mobileLoadIncrement, total);
          var slice = sorted.slice(start, end);
          var frag = document.createDocumentFragment();
          if (this.expandable) {
            // Los lotes que se APILAN nunca traen nada abierto por defecto: el
            // producto por defecto ya se decidió en el primer render y abrir
            // uno más a mitad de scroll movería el contenido bajo el dedo.
            for (var g = 0; g < slice.length; g++) this.appendProductGroup(frag, slice[g], false);
          } else {
            for (var i = 0; i < slice.length; i++) frag.appendChild(this.buildRow(slice[i]));
          }
          this.tbody.appendChild(frag);
          this.currentMobileShown = end;
          this.updateMobileLoadMoreUI();
        }

        /* ── Row builder ──────────────────────────────────────────── */

        buildRow(row) {
          var tr = document.createElement('tr');
          tr.className = 'surisa-catalog__row';
          if (row.id != null) tr.dataset.productId = String(row.id);
          if (row.url) tr.dataset.productUrl = row.url;
          if (row.handle) tr.dataset.productHandle = row.handle;
          // v0.9.0 — data-attrs leídos por handlers de qty stepper + reescritura
          // de hrefs Add/Consultar. Mantener sincronizados con los emitidos por
          // el SSR del <tr> (sections/page-catalogo.liquid).
          if (row.variant_id != null)  tr.dataset.variantId   = String(row.variant_id);
          if (row.moq != null)         tr.dataset.moq         = String(row.moq);
          if (row.stock_status)        tr.dataset.stockStatus = row.stock_status;
          if (row.ref)                 tr.dataset.ref         = row.ref;
          var self = this;
          var sortKey = this.state.sortKey;
          this.catalogData.columns.forEach(function (col) {
            var td = document.createElement('td');
            td.className = 'surisa-catalog__td surisa-catalog__td--' + col.key;
            td.dataset.colKey = col.key;
            if (col.key === 'image' || col.key === 'erp_code') {
              td.classList.add('surisa-catalog__td--sticky');
            }
            if (col.key === sortKey) {
              td.classList.add('surisa-catalog__td--active');
            }
            self.fillCell(td, col, row);
            tr.appendChild(td);
          });
          return tr;
        }

        /** URL de la fila con la variante siempre pegada. La celda de
         *  referencia enlazaba a `product.url` a secas y perdía la variante:
         *  inocuo en muelles (1 producto = 1 variante, así que ?variant=
         *  resuelve a la única que hay) pero rompe el flujo entero en las
         *  familias donde la referencia vendible ES la variante. Se arregla
         *  para los dos modos. */
        rowHref(row) {
          var url = row.url || '';
          if (!url) return '#';
          if (row.variant_id == null) return url;
          if (url.indexOf('variant=') !== -1) return url;
          return url + (url.indexOf('?') === -1 ? '?' : '&') + 'variant=' + row.variant_id;
        }

        fillCell(td, col, row) {
          var cells = row.cells || {};
          switch (col.key) {
            case 'image': {
              var a = document.createElement('a');
              a.href = row.url || '#';
              a.className = 'surisa-catalog__cell-image-link';
              if (row.image) {
                var img = document.createElement('img');
                img.src = row.image;
                img.width = 48;
                img.height = 48;
                img.alt = '';
                img.loading = 'lazy';
                img.className = 'surisa-catalog__cell-image';
                a.appendChild(img);
              } else {
                a.innerHTML =
                  '<span class="surisa-catalog__cell-image-placeholder" aria-hidden="true">' +
                  '<svg width="36" height="36" viewBox="0 0 32 32">' +
                  '<circle cx="16" cy="16" r="14" fill="#cdd2d9" stroke="#0b1220" stroke-width="0.4"/>' +
                  '<circle cx="16" cy="16" r="6" fill="#f1ede5" stroke="#0b1220" stroke-width="0.4"/>' +
                  '</svg></span>';
              }
              td.appendChild(a);
              break;
            }
            case 'erp_code': {
              var aSku = document.createElement('a');
              aSku.href = this.rowHref(row);
              aSku.className = 'surisa-catalog__cell-sku-link';
              aSku.textContent = cells.erp_code || row.ref || '—';
              td.appendChild(aSku);
              break;
            }
            case 'material': {
              td.textContent = cells.material != null ? String(cells.material) : '—';
              break;
            }
            case 'ball_bearing_type':
            case 'ball_bearing_designation': {
              // list.single_line_text → array de tokens en el dataset.
              // extractChoiceValues aplana cualquier forma; join con " / ";
              // vacío → "—". Coherente con el SSR de page-catalogo.liquid
              // (designation en .tnum, type en texto plano). designation: 0–3
              // códigos de rodamiento por SKU; ball_bearing_type slotted/plain.
              var bbVals = extractChoiceValues(cells[col.key]);
              if (bbVals.length === 0) {
                td.textContent = '—';
              } else if (col.key === 'ball_bearing_designation') {
                var bbSpan = document.createElement('span');
                bbSpan.className = 'tnum';
                bbSpan.textContent = bbVals.join(' / ');
                td.appendChild(bbSpan);
              } else {
                td.textContent = bbVals.join(' / ');
              }
              break;
            }
            case 'price': {
              // Cuando el precio es 0 (no asignado / pendiente cotización)
              // pintamos "Consultar" en cursiva + muted en lugar de "€0,00".
              // Misma lógica replicada en el SSR Liquid de los sections de
              // catálogo (page-catalogo-*.liquid case 'price') — mantener
              // las dos sincronizadas. El locale key vive en
              // sections.catalogo.cells.price_consultar y se inyecta al
              // componente via el atributo data-i18n-price-consultar.
              var priceSpan = document.createElement('span');
              if (Number(cells.price) === 0) {
                priceSpan.className = 'surisa-catalog__cell-price surisa-catalog__cell-price--consultar';
                priceSpan.textContent = this.i18nPriceConsultar || 'Consultar';
              } else {
                priceSpan.className = 'surisa-catalog__cell-price';
                priceSpan.textContent = cells.price_formatted || (cells.price != null ? cells.price : '—');
                // Sufijo de unidad de venta (v0.13) — sólo si el producto o su
                // variante declaran custom.supply_format ("sobre de 10 uds.").
                // Espejo del SSR (page-catalogo.liquid case 'price'). Los 6
                // catálogos de muelles tienen el metafield vacío ⇒ row.supply_format
                // es null ⇒ la celda queda idéntica a pre-v0.13.
                var sf = row.supply_format;
                if (sf && this.i18nPriceUnitFormat) {
                  var sfSpan = document.createElement('span');
                  sfSpan.className = 'surisa-catalog__cell-price-unit';
                  sfSpan.textContent = ' ' + this.i18nPriceUnitFormat.replace(/__FORMAT__/g, sf);
                  priceSpan.appendChild(sfSpan);
                }
              }
              td.appendChild(priceSpan);
              break;
            }
            case 'stock': {
              // Estado binario (ver derivación SSR en page-catalogo.liquid ~766):
              // in_stock (stock ≥ MOQ) → "En stock"; cualquier otro (stock < MOQ) → "Bajo pedido".
              var status = row.stock_status || 'out_of_stock';
              var label = this.i18nStockIn;
              if (status !== 'in_stock') label = this.i18nStockOut;
              var badge = document.createElement('span');
              badge.className = 'surisa-catalog__stock-badge';
              badge.dataset.stockStatus = status;
              var dot = document.createElement('span');
              dot.className = 'surisa-catalog__stock-dot';
              dot.setAttribute('aria-hidden', 'true');
              badge.appendChild(dot);
              badge.appendChild(document.createTextNode(' ' + label));
              // Unidades disponibles entre paréntesis sólo cuando hay stock.
              // Espejo del SSR (page-catalogo.liquid stock cell). in_stock ⇒
              // inventory_quantity ≥ MOQ ≥ 1, así que siempre es un entero > 0.
              if (status === 'in_stock') {
                var invQty = row.inventory_quantity;
                if (invQty != null && invQty !== '' && Number(invQty) > 0) {
                  var qtySpan = document.createElement('span');
                  qtySpan.className = 'surisa-catalog__stock-qty';
                  qtySpan.textContent = ' (' + formatThousandsEs(invQty) + ')';
                  badge.appendChild(qtySpan);
                }
              }
              td.appendChild(badge);
              break;
            }
            case 'quantity': {
              var qtyMoq = (row.moq != null) ? Number(row.moq) : 1;
              if (!isFinite(qtyMoq) || qtyMoq < 1) qtyMoq = 1;
              // Comprabilidad por PRECIO (no por stock): precio > 0 → comprable
              // aunque no haya stock (fabricación bajo pedido). Debe coincidir con
              // el SSR (page-catalogo.liquid is_buyable) y con el price cell
              // (precio 0 → "Consultar"). Requiere inventory_policy 'continue'.
              var qtyDisabled = !(Number(cells.price) > 0);
              var qtyRef = row.ref || '';

              var qtyWrap = document.createElement('div');
              qtyWrap.className = 'surisa-catalog__qty';
              qtyWrap.dataset.disabled = qtyDisabled ? 'true' : 'false';

              var stepper = document.createElement('div');
              stepper.className = 'surisa-catalog__qty-stepper';

              var btnDec = document.createElement('button');
              btnDec.type = 'button';
              btnDec.className = 'surisa-catalog__qty-btn';
              btnDec.setAttribute('data-qty-decrement', '');
              btnDec.setAttribute('aria-label', this.i18nQtyDecAria);
              btnDec.textContent = '−';

              var qtyInput = document.createElement('input');
              qtyInput.type = 'number';
              qtyInput.className = 'surisa-catalog__qty-input';
              qtyInput.setAttribute('data-qty-input', '');
              qtyInput.min = String(qtyMoq);
              qtyInput.step = '1';
              qtyInput.value = String(qtyMoq);
              qtyInput.setAttribute('inputmode', 'numeric');
              qtyInput.setAttribute(
                'aria-label',
                this.i18nQtyInputAria.replace(/__REF__/g, qtyRef)
              );

              var btnInc = document.createElement('button');
              btnInc.type = 'button';
              btnInc.className = 'surisa-catalog__qty-btn';
              btnInc.setAttribute('data-qty-increment', '');
              btnInc.setAttribute('aria-label', this.i18nQtyIncAria);
              btnInc.textContent = '+';

              if (qtyDisabled) {
                btnDec.disabled = true; btnDec.setAttribute('aria-disabled', 'true');
                qtyInput.disabled = true; qtyInput.setAttribute('aria-disabled', 'true');
                btnInc.disabled = true; btnInc.setAttribute('aria-disabled', 'true');
              }

              stepper.appendChild(btnDec);
              stepper.appendChild(qtyInput);
              stepper.appendChild(btnInc);
              qtyWrap.appendChild(stepper);

              if (!qtyDisabled) {
                var caption = document.createElement('span');
                caption.className = 'surisa-catalog__qty-caption';
                caption.setAttribute('data-qty-caption', '');
                caption.textContent = (qtyMoq === 1)
                  ? this.i18nMoqCaptionOne
                  : this.i18nMoqCaptionOther.replace(/__N__/g, String(qtyMoq));
                qtyWrap.appendChild(caption);
              }

              td.appendChild(qtyWrap);
              break;
            }
            case 'actions': {
              // Comprar activo si el SKU es comprable (precio > 0), aunque no haya
              // stock. Espejo del SSR (is_buyable) y del qty cell. Consultar pasa a
              // secundario (outline); cuando NO es comprable, Consultar es main.
              var actDisabled  = !(Number(cells.price) > 0);
              var actMoq       = (row.moq != null) ? Number(row.moq) : 1;
              if (!isFinite(actMoq) || actMoq < 1) actMoq = 1;
              var actRef       = row.ref || '';
              var actHandle    = row.handle || '';
              var actVariantId = row.variant_id != null ? String(row.variant_id) : '';

              // Consulta template: resolvemos __REF__ aquí (no cambia per-fila),
              // dejamos __QTY__ sin resolver para que el JS lo reescriba cuando
              // el usuario modifica el input de cantidad.
              var consultaTemplate = this.consultaUrl || '';
              if (consultaTemplate.indexOf('__REF__') !== -1) {
                consultaTemplate = consultaTemplate.replace(/__REF__/g, actRef);
              }
              // Base = `product.url` del dataset, que ya trae el prefijo de idioma
              // (/en/, /pt/). Montarla a mano con '/products/' + handle mandaba
              // al visitante de EN/PT a la ficha en español. Se quita la query
              // porque las filas de variante ya llevan ?variant= pegado.
              var actBase     = (row.url || ('/products/' + actHandle)).split('?')[0];
              var addTemplate = actBase + '?variant=' + actVariantId + '&quantity=__QTY__';
              var addHref     = addTemplate.replace(/__QTY__/g, String(actMoq));
              var consultaHref = consultaTemplate.replace(/__QTY__/g, String(actMoq));
              var consultarVariant = actDisabled ? 'main' : 'outline';

              var actWrap = document.createElement('div');
              actWrap.className = 'surisa-catalog__actions';

              var addEl;
              if (actDisabled) {
                addEl = document.createElement('button');
                addEl.type = 'button';
                addEl.disabled = true;
                addEl.setAttribute('aria-disabled', 'true');
                addEl.setAttribute('title', this.i18nAddDisabledTooltip);
                addEl.setAttribute('aria-label', this.i18nAddDisabledTooltip);
              } else {
                addEl = document.createElement('a');
                addEl.href = addHref;
                addEl.setAttribute('data-add-template', addTemplate);
                addEl.setAttribute(
                  'aria-label',
                  this.i18nAddAria.replace(/__REF__/g, actRef).replace(/__QTY__/g, String(actMoq))
                );
              }
              addEl.className = 'surisa-catalog__btn surisa-catalog__btn--add';
              addEl.setAttribute('data-add-cta', '');
              addEl.innerHTML =
                '<svg class="surisa-catalog__btn-icon" aria-hidden="true" viewBox="0 0 20 20" width="14" height="14">' +
                '<path d="M2 4h2l2 10h9l2-7H6" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>' +
                '<circle cx="8" cy="17" r="1.2" fill="currentColor"/>' +
                '<circle cx="15" cy="17" r="1.2" fill="currentColor"/>' +
                '</svg>';
              addEl.appendChild(document.createTextNode(this.i18nAddLabel));
              actWrap.appendChild(addEl);

              var consultarEl = document.createElement('a');
              consultarEl.className =
                'surisa-catalog__btn surisa-catalog__btn--consultar surisa-catalog__btn--' + consultarVariant;
              consultarEl.setAttribute('data-consultar-cta', '');
              consultarEl.setAttribute('data-consultar-template', consultaTemplate);
              consultarEl.href = consultaHref;
              consultarEl.setAttribute('aria-label', this.i18nConsultarAria.replace(/__REF__/g, actRef));
              consultarEl.innerHTML =
                '<svg class="surisa-catalog__btn-icon" aria-hidden="true" viewBox="0 0 20 20" width="14" height="14">' +
                '<path d="M3 5h14v10H3z M3 5l7 5 7-5" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>' +
                '</svg>';
              consultarEl.appendChild(document.createTextNode(this.i18nConsultarLabel));
              actWrap.appendChild(consultarEl);

              td.appendChild(actWrap);
              break;
            }
            default: {
              // Default cell render — lookup cells[resolveCellKey] + decimals +
              // value_prefix/value_suffix del column_def (v0.12 patrón B).
              //
              // value_prefix: ej. "M" para bolt_size (DIN 6796) → "M3", "M4"…
              // value_suffix: reservado para sufijos como "h", "ud" en familias
              //               futuras. Vacío por defecto.
              // El número se sigue formateando con decimals_mm/in del column_def
              // cuando v parsea a Number; cuando no parsea (string libre, e.g.
              // bolt_size_imperial "1-3/8\""), se renderiza tal cual con
              // prefix/suffix aplicados.
              var cellKey = resolveCellKey(col, this.state.unit);
              var v = cells[cellKey];
              if (v == null || v === '') {
                td.textContent = '—';
              } else {
                var decimals = this.state.unit === 'in' && col.decimals_in != null
                  ? col.decimals_in
                  : (col.decimals_mm != null ? col.decimals_mm : 1);
                var prefix = col.value_prefix || '';
                var suffix = col.value_suffix || '';
                var num = Number(v);
                var s = document.createElement('span');
                s.className = 'tnum';
                var body = isNaN(num) ? String(v) : num.toFixed(decimals);
                s.textContent = prefix + body + suffix;
                td.appendChild(s);
              }
            }
          }
        }

        /* ── Qty stepper handlers (v0.9.0) ──────────────────────────── */
        // Listeners delegados a nivel <surisa-catalog-table> (registrados en
        // connectedCallback) — cubren rows SSR y rows hidratadas por
        // paginación cliente sin necesidad de re-bindear al rebuild del tbody.

        handleQtyStepperClick(e) {
          var btn = e.target && e.target.closest
            ? e.target.closest('[data-qty-decrement], [data-qty-increment]')
            : null;
          if (!btn || btn.disabled) return;
          var tr = btn.closest('tr');
          if (!tr) return;
          var input = tr.querySelector('[data-qty-input]');
          if (!input || input.disabled) return;
          var current = parseInt(input.value, 10);
          var min = parseInt(input.min, 10) || 1;
          var step = parseInt(input.step, 10) || 1;
          if (isNaN(current)) current = min;
          var next = current + (btn.hasAttribute('data-qty-increment') ? step : -step);
          if (next < min) next = min;
          input.value = String(next);
          this.rewriteRowCtaHrefs(tr, next);
        }

        handleQtyInputChange(e) {
          var input = e.target;
          if (!input || !input.matches || !input.matches('[data-qty-input]')) return;
          var tr = input.closest('tr');
          if (!tr) return;
          var n = parseInt(input.value, 10);
          // input fires en cada keystroke — ignoramos estados intermedios
          // inválidos para no escribir hrefs basura. El clamp final ocurre
          // en `change` (handleQtyInputBlur) cuando el field se commitea.
          if (isNaN(n) || n < 1) return;
          this.rewriteRowCtaHrefs(tr, n);
        }

        handleQtyInputBlur(e) {
          var input = e.target;
          if (!input || !input.matches || !input.matches('[data-qty-input]')) return;
          var tr = input.closest('tr');
          if (!tr) return;
          var min = parseInt(input.min, 10) || 1;
          var n = parseInt(input.value, 10);
          if (isNaN(n) || n < min) {
            n = min;
            input.value = String(n);
          }
          this.rewriteRowCtaHrefs(tr, n);
        }

        rewriteRowCtaHrefs(tr, qty) {
          var qtyStr = String(qty);
          var ref = tr.dataset.ref || '';
          var addLink = tr.querySelector('[data-add-cta][data-add-template]');
          if (addLink) {
            var addTpl = addLink.getAttribute('data-add-template') || '';
            addLink.href = addTpl.replace(/__QTY__/g, qtyStr);
            if (this.i18nAddAria) {
              addLink.setAttribute(
                'aria-label',
                this.i18nAddAria.replace(/__REF__/g, ref).replace(/__QTY__/g, qtyStr)
              );
            }
          }
          var consultarLink = tr.querySelector('[data-consultar-cta][data-consultar-template]');
          if (consultarLink) {
            var consultarTpl = consultarLink.getAttribute('data-consultar-template') || '';
            consultarLink.href = consultarTpl.replace(/__QTY__/g, qtyStr);
          }
        }
      }
    );
  }

  /* ── makeChevron helper (used by sort active header) ─────────────── */

  function makeChevron(dir) {
    var span = document.createElement('span');
    span.className = 'surisa-catalog__th-chevron';
    span.dataset.direction = dir;
    span.setAttribute('aria-hidden', 'true');
    span.innerHTML =
      '<svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" ' +
      'stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">' +
      '<path d="M2.5 6.5L5 4l2.5 2.5"/></svg>';
    return span;
  }

  /* ── makeNavLink helper (used by pagination) ─────────────────────── */

  function makeNavLink(label, disabled, onClick, extraClass) {
    if (disabled) {
      var span = document.createElement('span');
      span.className = 'surisa-catalog__pagination-link surisa-catalog__pagination-link--disabled'
        + (extraClass ? ' ' + extraClass : '');
      span.setAttribute('aria-hidden', 'true');
      span.textContent = label;
      return span;
    }
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'surisa-catalog__pagination-link' + (extraClass ? ' ' + extraClass : '');
    btn.textContent = label;
    btn.addEventListener('click', onClick);
    return btn;
  }

  /* ── <surisa-sort-popover> ───────────────────────────────────────── */

  if (!customElements.get('surisa-sort-popover')) {
    customElements.define(
      'surisa-sort-popover',
      class SurisaSortPopover extends HTMLElement {
        connectedCallback() {
          this.sectionId    = this.getAttribute('data-section-id') || '';
          this.toggleBtn    = this.querySelector('[data-popover-toggle]');
          this.panel        = this.querySelector('[data-popover-panel]');
          this.listEl       = this.querySelector('[data-popover-list]');
          this.dirBtns      = Array.from(this.querySelectorAll('[data-popover-dir]'));
          this.clearBtn     = this.querySelector('[data-popover-clear]');
          this.currentLabel = this.querySelector('[data-current-label]');

          this.columns = [];
          this.state = { sortKey: null, sortDir: 'asc' };

          if (this.toggleBtn) this.toggleBtn.addEventListener('click', this.handleToggle.bind(this));
          this.dirBtns.forEach(function (btn) {
            btn.addEventListener('click', this.handleDirClick.bind(this));
          }.bind(this));
          if (this.clearBtn) this.clearBtn.addEventListener('click', this.handleClear.bind(this));

          // Outside click + ESC close
          this._onDocClick = this.handleDocClick.bind(this);
          this._onKey      = this.handleKey.bind(this);

          // Sync to table state
          this._handleCatalogEvent = this.handleCatalogEvent.bind(this);
          document.addEventListener('surisa-catalog:ready', this._handleCatalogEvent);
          document.addEventListener('surisa-catalog:state-change', this._handleCatalogEvent);

          // Proactive resync: if the matching <surisa-catalog-table> upgraded
          // BEFORE this popover did (microtask order depends on the relative
          // order of customElements.define() calls), the `ready` event fired
          // before we registered the listener and the columns array stays
          // empty. Query the table's current state directly to recover.
          var tableSelector = this.sectionId
            ? 'surisa-catalog-table[data-section-id="' + cssEscape(this.sectionId) + '"]'
            : 'surisa-catalog-table';
          var existing = document.querySelector(tableSelector);
          if (existing && existing.catalogData) {
            this.handleCatalogEvent({
              detail: {
                sectionId: existing.sectionId,
                columns: existing.catalogData.columns,
                state: Object.assign({}, existing.state)
              }
            });
          }
        }

        disconnectedCallback() {
          document.removeEventListener('click', this._onDocClick);
          document.removeEventListener('keydown', this._onKey);
          if (this._handleCatalogEvent) {
            document.removeEventListener('surisa-catalog:ready', this._handleCatalogEvent);
            document.removeEventListener('surisa-catalog:state-change', this._handleCatalogEvent);
          }
        }

        /* ── State sync with the catalog table ────────────────────── */

        handleCatalogEvent(evt) {
          if (!evt.detail) return;
          if (evt.detail.sectionId && this.sectionId && evt.detail.sectionId !== this.sectionId) return;
          if (evt.detail.columns) this.columns = evt.detail.columns;
          if (evt.detail.state) this.state = Object.assign({}, evt.detail.state);
          this.renderList();
          this.renderCurrentLabel();
          this.renderDirButtons();
        }

        renderList() {
          if (!this.listEl) return;
          while (this.listEl.firstChild) this.listEl.removeChild(this.listEl.firstChild);
          var sortable = this.columns.filter(function (c) { return c.sortable; });
          if (sortable.length === 0) return;
          var self = this;
          sortable.forEach(function (col) {
            var li = document.createElement('li');
            li.className = 'surisa-catalog__sort-popover-item';
            li.setAttribute('role', 'option');
            var btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'surisa-catalog__sort-popover-item-btn';
            btn.dataset.colKey = col.key;
            btn.textContent = col.label || col.key;
            if (col.key === self.state.sortKey) {
              btn.classList.add('is-active');
              btn.setAttribute('aria-selected', 'true');
              li.setAttribute('aria-selected', 'true');
            } else {
              btn.setAttribute('aria-selected', 'false');
            }
            btn.addEventListener('click', function () {
              self.dispatchChange({ sortKey: col.key, sortDir: self.state.sortDir || 'asc' });
              self.close();
            });
            li.appendChild(btn);
            self.listEl.appendChild(li);
          });
        }

        renderCurrentLabel() {
          if (!this.currentLabel) return;
          var arrowEl = this.querySelector('[data-current-arrow]');
          // Mismo patrón que el Filters CTA: toggle `data-has-sort` en el host
          // para que CSS pinte el border navy cuando el sort está activo.
          this.setAttribute('data-has-sort', this.state.sortKey ? 'true' : 'false');
          if (!this.state.sortKey) {
            this.currentLabel.textContent = '—';
            if (arrowEl) arrowEl.hidden = true;
            return;
          }
          var col = this.columns.find(function (c) { return c.key === this.state.sortKey; }.bind(this));
          this.currentLabel.textContent = col && col.label ? col.label : this.state.sortKey;
          // Direction arrow al lado del label: visible cuando hay sortKey,
          // rotado vía CSS según data-dir.
          if (arrowEl) {
            arrowEl.hidden = false;
            arrowEl.setAttribute('data-dir', this.state.sortDir === 'desc' ? 'desc' : 'asc');
          }
        }

        renderDirButtons() {
          var active = this.state.sortDir === 'desc' ? 'desc' : 'asc';
          this.dirBtns.forEach(function (btn) {
            var dir = btn.getAttribute('data-popover-dir');
            var isActive = dir === active;
            btn.classList.toggle('is-active', isActive);
            btn.setAttribute('aria-checked', isActive ? 'true' : 'false');
          });
        }

        /* ── UI handlers ──────────────────────────────────────────── */

        handleToggle(evt) {
          evt.preventDefault();
          if (this.panel.hidden) this.open();
          else this.close();
        }

        handleDirClick(evt) {
          var btn = evt.currentTarget;
          var dir = btn.getAttribute('data-popover-dir');
          if (dir !== 'asc' && dir !== 'desc') return;
          // Apply only if a sortKey is active; otherwise just preview the dir.
          if (this.state.sortKey) {
            this.dispatchChange({ sortKey: this.state.sortKey, sortDir: dir });
          } else {
            this.state.sortDir = dir;
            this.renderDirButtons();
          }
        }

        handleClear() {
          this.dispatchChange({ sortKey: null, sortDir: 'asc' });
          this.close();
        }

        handleDocClick(evt) {
          if (this.contains(evt.target)) return;
          this.close();
        }

        handleKey(evt) {
          if (evt.key === 'Escape') {
            this.close();
            if (this.toggleBtn) this.toggleBtn.focus();
          }
        }

        open() {
          if (!this.panel) return;
          this.panel.hidden = false;
          if (this.toggleBtn) this.toggleBtn.setAttribute('aria-expanded', 'true');
          // Defer listeners so the initiating click doesn't close immediately.
          setTimeout(function () {
            document.addEventListener('click', this._onDocClick);
            document.addEventListener('keydown', this._onKey);
          }.bind(this), 0);
        }

        close() {
          if (!this.panel) return;
          this.panel.hidden = true;
          if (this.toggleBtn) this.toggleBtn.setAttribute('aria-expanded', 'false');
          document.removeEventListener('click', this._onDocClick);
          document.removeEventListener('keydown', this._onKey);
        }

        /* ── Outbound event ───────────────────────────────────────── */

        dispatchChange(detail) {
          this.dispatchEvent(new CustomEvent('surisa-sort:change', {
            bubbles: true,
            detail: Object.assign({ sectionId: this.sectionId }, detail)
          }));
        }
      }
    );
  }

  /* ── Bridge: <surisa-sort-popover> → <surisa-catalog-table>.setState ── */

  document.addEventListener('surisa-sort:change', function (evt) {
    if (!evt.detail) return;
    var sectionId = evt.detail.sectionId || '';
    var table = sectionId
      ? document.querySelector('surisa-catalog-table[data-section-id="' + cssEscape(sectionId) + '"]')
      : document.querySelector('surisa-catalog-table');
    if (!table) return;
    table.setState({
      sortKey: evt.detail.sortKey,
      sortDir: evt.detail.sortDir
    });
  });

  function cssEscape(s) {
    if (window.CSS && window.CSS.escape) return window.CSS.escape(s);
    return String(s).replace(/[^a-zA-Z0-9_-]/g, function (c) {
      return '\\' + c;
    });
  }

  /* ── <surisa-catalog-filters> ────────────────────────────────────── */
  /*
     Wrapper de filtros sidebar (desktop) / drawer overlay (mobile).
     Mismo DOM en ambos breakpoints; el CSS cambia el layout. JS gestiona:

     - bind de checkbox facets (renderizados dinámicamente desde dataset rows)
     - bind de range facets (slider 2-thumb superpuesto, anti-cross)
     - open/close del drawer mobile + focus trap + ESC + body scroll lock
     - sync del state desde catalog table events (incluyendo URL params al load)
     - emisión del evento `surisa-catalog-filters:change` con state.filters
       reconstruido desde el DOM (single source of truth = DOM checkbox/sliders) */

  if (!customElements.get('surisa-catalog-filters')) {
    customElements.define(
      'surisa-catalog-filters',
      class SurisaCatalogFilters extends HTMLElement {
        constructor() {
          super();
          this.state = { filters: {} };
          this.catalogData = null;
          this.isOpen = false;
          this._previousFocus = null;
        }

        connectedCallback() {
          this.sectionId = this.getAttribute('data-section-id') || '';

          this.panel       = this.querySelector('[data-filters-panel]');
          this.backdrop    = this.querySelector('[data-filters-backdrop]');
          this.closeBtn    = this.querySelector('[data-filters-close]');
          this.resetBtns   = Array.from(this.querySelectorAll('[data-filters-reset]'));
          this.applyBtn    = this.querySelector('[data-filters-apply]');
          this.applyLabel  = this.querySelector('[data-apply-label]');
          // Facets = ALL filter containers across desktop pills + mobile drawer.
          // Both share data-filter-key + data-filter-type so behavior code is unified.
          this.facets      = Array.from(this.querySelectorAll('[data-filter-key][data-filter-type]'));
          // Pills = desktop-only filter wrappers (have a [data-pill-toggle] child).
          this.pills       = Array.from(this.querySelectorAll('.surisa-catalog__pill-wrapper'));
          this.clearAllBtn = this.querySelector('[data-filters-clear-all]');

          // i18n templates
          this.i18nApplyOne    = this.getAttribute('data-i18n-apply-one')    || '';
          this.i18nApplyOther  = this.getAttribute('data-i18n-apply-other')  || '';
          this.i18nActiveOne   = this.getAttribute('data-i18n-active-one')   || '';
          this.i18nActiveOther = this.getAttribute('data-i18n-active-other') || '';
          this.i18nMultiOne    = this.getAttribute('data-i18n-multi-selected-one')   || '';
          this.i18nMultiOther  = this.getAttribute('data-i18n-multi-selected-other') || '';
          // Modo avanzado (facetas con muchos valores): buscador + chips.
          this.i18nSearchPlaceholder = this.getAttribute('data-i18n-search-placeholder') || '';
          this.i18nChipRemoveValue   = this.getAttribute('data-i18n-chip-remove-aria')   || '';

          // Section-level lookups (mobile trigger button + active count badge
          // live OUTSIDE the wrapper, inside the table section).
          this.sectionEl = this.closest('.section-catalogo-muelles-platillo, [id^="shopify-section-"]') || document;
          this.triggerBtn   = this.sectionEl.querySelector('[data-action="open-filters"]');
          this.activeBadge  = this.sectionEl.querySelector('[data-active-count]');

          // Cache the default label rendered SSR — we need it back when the
          // filter clears (pill returns to its inactive state).
          this.pills.forEach(function (pill) {
            var valueEl = pill.querySelector('[data-pill-value]');
            if (valueEl) pill._defaultLabel = valueEl.textContent.trim();
          });

          var self = this;

          // Bind facets — range immediately; checkbox waits for dataset.
          this.facets.forEach(function (facet) {
            var type = facet.getAttribute('data-filter-type');
            if (type === 'range') self.bindRangeFacet(facet);
            // Checkbox bindings happen in renderCheckboxOptions, after rows arrive.
          });

          // Bind desktop pill UI: toggle popover, close (X), reset link.
          this.pills.forEach(function (pill) { self.bindPill(pill); });

          // UI controls
          this.resetBtns.forEach(function (btn) {
            btn.addEventListener('click', function () { self.handleReset(); });
          });
          if (this.closeBtn) this.closeBtn.addEventListener('click', function () { self.close(); });
          if (this.applyBtn) this.applyBtn.addEventListener('click', function () { self.close(); });
          if (this.backdrop) this.backdrop.addEventListener('click', function () { self.close(); });
          if (this.clearAllBtn) {
            this.clearAllBtn.addEventListener('click', function (evt) {
              evt.preventDefault();
              self.clearAllFilters();
            });
          }

          // Mobile trigger button (lives outside this wrapper, listen at document).
          this._onTriggerClick = this.handleTriggerClick.bind(this);
          document.addEventListener('click', this._onTriggerClick);

          // Outside-click for desktop pill popovers (cerrar popover open al click fuera).
          this._onDocClickForPills = this.handleDocClickForPills.bind(this);
          document.addEventListener('click', this._onDocClickForPills);

          // ESC key (cerrar drawer mobile abierto Y cerrar pill popover open).
          this._onKey = this.handleKey.bind(this);
          document.addEventListener('keydown', this._onKey);

          // Listen to catalog-table state.
          this._onCatalogEvent = this.handleCatalogEvent.bind(this);
          document.addEventListener('surisa-catalog:ready', this._onCatalogEvent);
          document.addEventListener('surisa-catalog:state-change', this._onCatalogEvent);

          // Proactive resync — if the matching <surisa-catalog-table> upgraded
          // before us, the `ready` event already fired and we need to read
          // its current state directly (same pattern as <surisa-sort-popover>).
          var tableSelector = this.sectionId
            ? 'surisa-catalog-table[data-section-id="' + cssEscape(this.sectionId) + '"]'
            : 'surisa-catalog-table';
          var existing = document.querySelector(tableSelector);
          if (existing && existing.catalogData) {
            this.handleCatalogEvent({
              detail: {
                sectionId:     existing.sectionId,
                columns:       existing.catalogData.columns,
                rows:          existing.catalogData.rows,
                filters:       existing.catalogData.filters || [],
                filteredCount: existing.getFilteredRows().length,
                state:         Object.assign({}, existing.state)
              }
            });
          }
        }

        disconnectedCallback() {
          document.removeEventListener('click', this._onTriggerClick);
          if (this._onDocClickForPills) document.removeEventListener('click', this._onDocClickForPills);
          document.removeEventListener('keydown', this._onKey);
          document.removeEventListener('surisa-catalog:ready', this._onCatalogEvent);
          document.removeEventListener('surisa-catalog:state-change', this._onCatalogEvent);
        }

        /* ── Pill UI (desktop horizontal bar) ──────────────────────── */

        /** Bind handlers for a single pill: toggle popover open/close, the
         *  X close button that clears the filter without opening the popover,
         *  and the "Restablecer" link inside the popover header.
         *  Range inputs (number) and slider thumbs are bound in bindRangeFacet. */
        bindPill(pill) {
          var self = this;
          var toggleBtn = pill.querySelector('[data-pill-toggle]');
          var popover   = pill.querySelector('[data-pill-popover]');
          var closeBtn  = pill.querySelector('[data-pill-close]');
          var resetBtn  = pill.querySelector('[data-pill-reset]');

          if (toggleBtn) {
            toggleBtn.addEventListener('click', function (evt) {
              // If the click target was the close-X inside the button, don't toggle —
              // closeBtn's own handler runs first via stopPropagation.
              if (evt.defaultPrevented) return;
              self.togglePill(pill);
            });
          }

          if (closeBtn) {
            closeBtn.addEventListener('click', function (evt) {
              evt.preventDefault();
              evt.stopPropagation();
              self.clearPillFilter(pill);
            });
            // Keyboard activation for the close span (role=button).
            closeBtn.addEventListener('keydown', function (evt) {
              if (evt.key === 'Enter' || evt.key === ' ') {
                evt.preventDefault();
                evt.stopPropagation();
                self.clearPillFilter(pill);
              }
            });
          }

          if (resetBtn) {
            resetBtn.addEventListener('click', function (evt) {
              evt.preventDefault();
              evt.stopPropagation();
              self.clearPillFilter(pill);
              self.closePill(pill);
            });
          }

          // Stop clicks inside the popover panel from bubbling to the doc
          // outside-click listener that would close the popover.
          if (popover) {
            popover.addEventListener('click', function (evt) { evt.stopPropagation(); });
          }
        }

        /** Toggle the popover of a pill. Closes any other open pill popover. */
        togglePill(pill) {
          var popover = pill.querySelector('[data-pill-popover]');
          var toggleBtn = pill.querySelector('[data-pill-toggle]');
          if (!popover) return;
          var willOpen = popover.hidden;
          this.closeAllPopovers();
          if (willOpen) {
            popover.hidden = false;
            if (toggleBtn) toggleBtn.setAttribute('aria-expanded', 'true');
          }
        }

        /** Close a specific pill's popover. */
        closePill(pill) {
          var popover = pill.querySelector('[data-pill-popover]');
          var toggleBtn = pill.querySelector('[data-pill-toggle]');
          if (popover) popover.hidden = true;
          if (toggleBtn) toggleBtn.setAttribute('aria-expanded', 'false');
        }

        /** Close all open popovers across all pills. */
        closeAllPopovers() {
          var self = this;
          this.pills.forEach(function (p) { self.closePill(p); });
        }

        /** Outside-click handler: if a click lands outside any open popover
         *  AND outside the pill's toggle button, close the open popover.
         *  We also exclude clicks on the X (close) and reset link, which have
         *  their own handlers. */
        handleDocClickForPills(evt) {
          if (!this.pills || this.pills.length === 0) return;
          var openPill = this.pills.find(function (p) {
            var pop = p.querySelector('[data-pill-popover]');
            return pop && !pop.hidden;
          });
          if (!openPill) return;
          if (openPill.contains(evt.target)) return;
          this.closePill(openPill);
        }

        /** Clear the filter associated with a pill (delete state.filters[key]).
         *  Used by both the X close in the pill AND the Restablecer in popover.
         *
         *  Must clear BOTH the desktop pill AND the mobile drawer facet that
         *  share this data-filter-key. Otherwise readFiltersFromDom would
         *  still find the mirror set populated and emit a non-empty filter.
         *
         *  CRITICAL — range thumbs must snap to DISPLAY bounds (in the active
         *  unit), not to canonical mm bounds. After syncRangeFacetUnit set
         *  the inputs' min/max/step in inches (when state.unit==='in'),
         *  writing canonical mm values (e.g. value=650 with input.max=25.6)
         *  gets clamped by the browser to 25.6 (in) which then converts back
         *  to ~650 mm in readFiltersFromDom — emitting a stale filter that
         *  never clears. Use getDisplayBounds to write in the active unit. */
        clearPillFilter(pill) {
          var key = pill.getAttribute('data-filter-key');
          var type = pill.getAttribute('data-filter-type');
          if (!key || !type) return;
          var self = this;
          var siblings = this.facets.filter(function (f) {
            return f.getAttribute('data-filter-key') === key;
          });
          siblings.forEach(function (facet) {
            if (type === 'checkbox') {
              Array.from(facet.querySelectorAll('input[type="checkbox"]')).forEach(function (cb) {
                cb.checked = false;
              });
            } else if (type === 'range') {
              var displayBounds = self.getDisplayBounds(facet);
              var minInput = facet.querySelector('[data-range-thumb="min"]');
              var maxInput = facet.querySelector('[data-range-thumb="max"]');
              if (minInput) minInput.value = String(displayBounds.min);
              if (maxInput) maxInput.value = String(displayBounds.max);
            }
          });
          this.dispatchFromDom(pill);
          var unit = (this.state && this.state.unit) || 'mm';
          if (type === 'range') {
            siblings.forEach(function (f) { self.syncRangeFacetUnit(f, unit); });
          }
        }

        /** Clear-all: emit empty state.filters via dispatchFromDom after
         *  resetting every pill's UI. Same display-unit snap rule as
         *  clearPillFilter (see comment there). */
        clearAllFilters() {
          var self = this;
          this.facets.forEach(function (facet) {
            var type = facet.getAttribute('data-filter-type');
            if (type === 'checkbox') {
              Array.from(facet.querySelectorAll('input[type="checkbox"]')).forEach(function (cb) {
                cb.checked = false;
              });
            } else if (type === 'range') {
              var displayBounds = self.getDisplayBounds(facet);
              var minInput = facet.querySelector('[data-range-thumb="min"]');
              var maxInput = facet.querySelector('[data-range-thumb="max"]');
              if (minInput) minInput.value = String(displayBounds.min);
              if (maxInput) maxInput.value = String(displayBounds.max);
            }
          });
          this.closeAllPopovers();
          this.dispatchFromDom();
          var unit = (this.state && this.state.unit) || 'mm';
          this.facets.forEach(function (facet) {
            if (facet.getAttribute('data-filter-type') === 'range') {
              self.syncRangeFacetUnit(facet, unit);
            }
          });
        }

        /* ── Mobile trigger / drawer lifecycle ─────────────────────── */

        handleTriggerClick(evt) {
          var btn = evt.target.closest('[data-action="open-filters"]');
          if (!btn) return;
          // Only respond if the button belongs to our section.
          if (!this.sectionEl.contains(btn)) return;
          evt.preventDefault();
          this.open();
        }

        open() {
          if (this.isOpen) return;
          this.isOpen = true;
          this._previousFocus = document.activeElement;
          if (this.panel) this.panel.setAttribute('aria-modal', 'true');
          if (this.backdrop) this.backdrop.hidden = false;
          this.classList.add('is-open');
          document.body.classList.add('surisa-catalog-filters-open');
          if (this.triggerBtn) this.triggerBtn.setAttribute('aria-expanded', 'true');
          // Doc-click handler — robust close on backdrop click. Direct
          // backdrop.addEventListener didn't fire reliably (the wrapper's
          // own stacking context can swallow the click in some Chromium
          // builds when the panel is mid-transition). Listening at document
          // and excluding panel/trigger guarantees the close path.
          var self = this;
          this._onDocClickWhileOpen = function (evt) {
            if (!self.isOpen) return;
            if (self.panel && self.panel.contains(evt.target)) return;
            if (self.triggerBtn && self.triggerBtn.contains(evt.target)) return;
            self.close();
          };
          // Defer registration so the click that opened the drawer doesn't
          // immediately close it during event bubbling.
          setTimeout(function () {
            document.addEventListener('click', self._onDocClickWhileOpen);
          }, 0);
          // Focus the close button so screen readers + keyboard users land inside.
          if (this.closeBtn) {
            setTimeout(function () { this.closeBtn.focus(); }.bind(this), 80);
          }
        }

        close() {
          if (!this.isOpen) return;
          this.isOpen = false;
          if (this.panel) this.panel.setAttribute('aria-modal', 'false');
          if (this.backdrop) this.backdrop.hidden = true;
          this.classList.remove('is-open');
          document.body.classList.remove('surisa-catalog-filters-open');
          if (this.triggerBtn) this.triggerBtn.setAttribute('aria-expanded', 'false');
          if (this._onDocClickWhileOpen) {
            document.removeEventListener('click', this._onDocClickWhileOpen);
            this._onDocClickWhileOpen = null;
          }
          if (this._previousFocus && typeof this._previousFocus.focus === 'function') {
            this._previousFocus.focus();
          }
          this._previousFocus = null;
        }

        handleKey(evt) {
          if (evt.key !== 'Escape') return;
          // Priority: if a pill popover is open, close that first (desktop).
          // Otherwise close the mobile drawer if it's open.
          var openPill = this.pills && this.pills.find(function (p) {
            var pop = p.querySelector('[data-pill-popover]');
            return pop && !pop.hidden;
          });
          if (openPill) {
            evt.preventDefault();
            this.closePill(openPill);
            return;
          }
          if (this.isOpen) {
            evt.preventDefault();
            this.close();
          }
        }

        /* ── Range facet (slider 2-thumb · Opción B mm↔in) ─────────── */

        bindRangeFacet(facet) {
          // Canonical bounds (always mm in v0.8.0 — filter_def records are
          // `_mm` only by convention). Cached on the facet for syncRangeFacetUnit.
          facet._mmBounds = {
            min: Number(facet.getAttribute('data-range-min')),
            max: Number(facet.getAttribute('data-range-max'))
          };

          var minInput = facet.querySelector('[data-range-thumb="min"]');
          var maxInput = facet.querySelector('[data-range-thumb="max"]');
          var self = this;

          function onInput(evt) {
            var mn = Number(minInput.value);
            var mx = Number(maxInput.value);
            // Prevent thumbs crossing — clamp the one being dragged.
            if (mn > mx) {
              if (evt.target === minInput) {
                mn = mx;
                minInput.value = String(mx);
              } else {
                mx = mn;
                maxInput.value = String(mn);
              }
            }
            var unit = (self.state && self.state.unit) || 'mm';
            var minDisp = facet.querySelector('[data-range-display="min"]');
            var maxDisp = facet.querySelector('[data-range-display="max"]');
            if (minDisp) minDisp.textContent = formatRangeValue(mn, unit);
            if (maxDisp) maxDisp.textContent = formatRangeValue(mx, unit);
            // Repaint gradient using current display bounds (already in `unit`).
            var displayBounds = self.getDisplayBounds(facet);
            self.updateRangeFill(facet, mn, mx, displayBounds.min, displayBounds.max);
            // sourceFacet = this facet so readFiltersFromDom reads from here,
            // not from the mirror set (which has stale values until syncUiFromState).
            self.dispatchFromDom(facet);
          }

          if (minInput) minInput.addEventListener('input', onInput);
          if (maxInput) maxInput.addEventListener('input', onInput);

          // Editable number inputs (desktop popover only) — sync with slider.
          // Typing into them previews the slider thumb in real time and
          // commits via dispatchFromDom (immediate-apply).
          var minNum = facet.querySelector('[data-range-input="min"]');
          var maxNum = facet.querySelector('[data-range-input="max"]');

          function commitNumberInput(which) {
            var input = which === 'min' ? minNum : maxNum;
            var slider = which === 'min' ? minInput : maxInput;
            if (!input || !slider) return;
            var n = Number(input.value);
            if (isNaN(n)) return;
            // Clamp to bounds
            var displayBounds = self.getDisplayBounds(facet);
            if (n < displayBounds.min) n = displayBounds.min;
            if (n > displayBounds.max) n = displayBounds.max;
            // Prevent crossing
            var otherSlider = which === 'min' ? maxInput : minInput;
            if (otherSlider) {
              var other = Number(otherSlider.value);
              if (which === 'min' && n > other) n = other;
              if (which === 'max' && n < other) n = other;
            }
            input.value = String(n);
            slider.value = String(n);
            // Reuse the slider onInput path to repaint + dispatch.
            onInput({ target: slider });
          }

          // Live preview: as the user types in the number input, mirror to
          // the slider thumb AND repaint the gradient fill in real time.
          // We don't write back to the number input here (would jump the
          // cursor), and we don't dispatch setState (that fires on blur/Enter).
          function livePreview(which) {
            var input = which === 'min' ? minNum : maxNum;
            var slider = which === 'min' ? minInput : maxInput;
            if (!input || !slider) return;
            var raw = input.value;
            if (raw === '' || raw === '-') return;  // mid-typing; ignore.
            var n = Number(raw);
            if (isNaN(n)) return;
            var displayBounds = self.getDisplayBounds(facet);
            // Clamp to bounds + anti-cross (mirror commitNumberInput logic).
            if (n < displayBounds.min) n = displayBounds.min;
            if (n > displayBounds.max) n = displayBounds.max;
            var otherSlider = which === 'min' ? maxInput : minInput;
            if (otherSlider) {
              var other = Number(otherSlider.value);
              if (which === 'min' && n > other) n = other;
              if (which === 'max' && n < other) n = other;
            }
            slider.value = String(n);
            // Update the display label under the slider.
            var unit = (self.state && self.state.unit) || 'mm';
            var dispEl = facet.querySelector('[data-range-display="' + which + '"]');
            if (dispEl) dispEl.textContent = formatRangeValue(n, unit);
            // Repaint the gradient — this is the visible change Joan wanted live.
            self.updateRangeFill(
              facet,
              Number(minInput.value), Number(maxInput.value),
              displayBounds.min, displayBounds.max
            );
          }

          if (minNum) {
            minNum.addEventListener('input', function () { livePreview('min'); });
            minNum.addEventListener('blur',  function () { commitNumberInput('min'); });
            minNum.addEventListener('keydown', function (evt) {
              if (evt.key === 'Enter') { evt.preventDefault(); commitNumberInput('min'); minNum.blur(); }
            });
          }
          if (maxNum) {
            maxNum.addEventListener('input', function () { livePreview('max'); });
            maxNum.addEventListener('blur',  function () { commitNumberInput('max'); });
            maxNum.addEventListener('keydown', function (evt) {
              if (evt.key === 'Enter') { evt.preventDefault(); commitNumberInput('max'); maxNum.blur(); }
            });
          }

          // Initial sync to current unit (mm by default; in if URL-loaded).
          this.syncRangeFacetUnit(facet, (this.state && this.state.unit) || 'mm');
        }

        /** Re-render bounds, step, thumbs, display labels, and gradient fill
         *  for the active unit. Dimensional `_mm` filters convert to inches
         *  when state.unit === 'in'; everything else stays as-is. */
        syncRangeFacetUnit(facet, unit) {
          var bounds = facet._mmBounds || {
            min: Number(facet.getAttribute('data-range-min')),
            max: Number(facet.getAttribute('data-range-max'))
          };
          var key = facet.getAttribute('data-filter-key') || '';
          var isDim = isDimensionalMmKey(key);

          // Display bounds + step for the active unit.
          var minD, maxD, step;
          if (isDim && unit === 'in') {
            minD = +(bounds.min * MM_TO_IN).toFixed(2);
            maxD = +(bounds.max * MM_TO_IN).toFixed(2);
            step = 0.1;
          } else {
            minD = bounds.min;
            maxD = bounds.max;
            step = 1;
          }

          var minInput = facet.querySelector('[data-range-thumb="min"]');
          var maxInput = facet.querySelector('[data-range-thumb="max"]');
          if (!minInput || !maxInput) return;

          minInput.min = String(minD);
          minInput.max = String(maxD);
          minInput.step = String(step);
          maxInput.min = String(minD);
          maxInput.max = String(maxD);
          maxInput.step = String(step);

          // Current thumb values from state (state holds CANONICAL mm).
          var f = (this.state.filters || {})[key];
          var currentMinMm = (f && f.min != null) ? f.min : bounds.min;
          var currentMaxMm = (f && f.max != null) ? f.max : bounds.max;
          var currentMin = (isDim && unit === 'in') ? +(currentMinMm * MM_TO_IN).toFixed(2) : currentMinMm;
          var currentMax = (isDim && unit === 'in') ? +(currentMaxMm * MM_TO_IN).toFixed(2) : currentMaxMm;

          minInput.value = String(currentMin);
          maxInput.value = String(currentMax);

          // Sync the editable number inputs (desktop popover) with the same
          // bounds + values + step. They share state with the slider thumbs.
          var minNum = facet.querySelector('[data-range-input="min"]');
          var maxNum = facet.querySelector('[data-range-input="max"]');
          if (minNum) {
            minNum.min  = String(minD);
            minNum.max  = String(maxD);
            minNum.step = String(step);
            minNum.value = String(currentMin);
          }
          if (maxNum) {
            maxNum.min  = String(minD);
            maxNum.max  = String(maxD);
            maxNum.step = String(step);
            maxNum.value = String(currentMax);
          }

          var minDisp = facet.querySelector('[data-range-display="min"]');
          var maxDisp = facet.querySelector('[data-range-display="max"]');
          if (minDisp) minDisp.textContent = formatRangeValue(currentMin, isDim ? unit : 'mm');
          if (maxDisp) maxDisp.textContent = formatRangeValue(currentMax, isDim ? unit : 'mm');

          // Unit indicator in the facet header — only for dimensional facets.
          // (Mobile drawer facet uses .facet-unit; desktop popover uses .popover-range-unit.)
          var unitIndicator = facet.querySelector('.surisa-catalog__facet-unit, .surisa-catalog__popover-range-unit');
          if (unitIndicator && isDim) unitIndicator.textContent = unit;

          this.updateRangeFill(facet, currentMin, currentMax, minD, maxD);
        }

        /** Display bounds for the active unit (used by gradient + thumb crossing
         *  checks). Returns {min, max} in the unit the slider is currently
         *  rendering, not the canonical mm. */
        getDisplayBounds(facet) {
          var bounds = facet._mmBounds || {
            min: Number(facet.getAttribute('data-range-min')),
            max: Number(facet.getAttribute('data-range-max'))
          };
          var key = facet.getAttribute('data-filter-key') || '';
          var unit = (this.state && this.state.unit) || 'mm';
          if (isDimensionalMmKey(key) && unit === 'in') {
            return {
              min: +(bounds.min * MM_TO_IN).toFixed(2),
              max: +(bounds.max * MM_TO_IN).toFixed(2)
            };
          }
          return bounds;
        }

        updateRangeFill(facet, val1, val2, min, max) {
          var range = max - min;
          if (range <= 0) return;
          var startPct = ((val1 - min) / range) * 100;
          var endPct   = ((val2 - min) / range) * 100;
          // CRITICAL: assign to the .facet-range slider wrapper, NOT to the
          // facet <details> container. The facet <details> wraps the entire
          // summary + body + range — assigning backgroundImage to it would
          // paint the whole filter block. Earlier custom-properties approach
          // worked by accident: the props cascaded from <details> down and
          // the CSS gradient rule on .facet-range consumed them, so paint
          // only happened on the slider track. Direct backgroundImage
          // assignment needs to target the right element explicitly.
          var rangeEl = facet.querySelector('.surisa-catalog__facet-range');
          if (!rangeEl) return;
          rangeEl.style.backgroundImage =
            'linear-gradient(to right,' +
            ' rgba(11, 18, 32, 0.18) 0%,' +
            ' rgba(11, 18, 32, 0.18) ' + startPct + '%,' +
            ' #0b1220 ' + startPct + '%,' +
            ' #0b1220 ' + endPct + '%,' +
            ' rgba(11, 18, 32, 0.18) ' + endPct + '%,' +
            ' rgba(11, 18, 32, 0.18) 100%)';
        }

        /* ── Checkbox facet (rendered from dataset rows) ───────────── */

        renderCheckboxOptions(facet, rows) {
          var key = facet.getAttribute('data-filter-key');
          var body = facet.querySelector('[data-facet-options]');
          if (!body) return;

          // Counts per unique value via extractChoiceValues — handles every
          // metafield shape (single_line_text string, list.single_line_text
          // array, wrapped {value:...} objects, taxonomy {handle,label}, etc.).
          // Counts reflect "would-be" results if only this filter were active.
          // For dynamic intersection counts, refactor here.
          var counts = {};
          rows.forEach(function (r) {
            var raw = (r.cells || {})[key];
            extractChoiceValues(raw).forEach(function (s) {
              counts[s] = (counts[s] || 0) + 1;
            });
          });
          var values = Object.keys(counts).sort();
          var active = (this.state.filters || {})[key] || [];

          // Modo avanzado automático (Opción B): facetas con muchos valores
          // (>ADVANCED_THRESHOLD) incorporan buscador + chips de selección +
          // lista en columnas con scroll. El resto siguen como dropdown simple.
          // No requiere config en el metaobject: se decide aquí porque el nº de
          // valores distintos solo se conoce en cliente (se derivan de las filas).
          var ADVANCED_THRESHOLD = 15;
          var advanced = values.length > ADVANCED_THRESHOLD;

          var popover = facet.querySelector('[data-pill-popover]');
          if (popover) popover.classList.toggle('surisa-catalog__popover--advanced', advanced);

          while (body.firstChild) body.removeChild(body.firstChild);
          var self = this;

          function buildOption(val) {
            var label = document.createElement('label');
            label.className = 'surisa-catalog__facet-option';
            label.setAttribute('data-facet-option', '');
            label.setAttribute('data-value', val);

            var input = document.createElement('input');
            input.type = 'checkbox';
            input.value = val;
            input.setAttribute('data-filter-key', key);
            input.checked = active.indexOf(val) !== -1;
            input.addEventListener('change', function () {
              // Pass facet as sourceFacet so readFiltersFromDom reads the
              // freshly-updated state from this set (desktop pill OR mobile
              // drawer — whichever the user clicked), not the stale mirror.
              // withScrollPreserved: al añadirse el chip arriba del listado el
              // drawer móvil saltaba al fondo; preservamos el scroll.
              self.withScrollPreserved(facet, function () { self.dispatchFromDom(facet); });
            });

            var box = document.createElement('span');
            box.className = 'surisa-catalog__facet-checkbox';
            box.setAttribute('aria-hidden', 'true');
            box.innerHTML =
              '<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
              'stroke-width="3.2" stroke-linecap="round" stroke-linejoin="round">' +
              '<path d="M5 12l5 5 9-12"/></svg>';

            var text = document.createElement('span');
            text.className = 'surisa-catalog__facet-option-label';
            text.textContent = val;

            var count = document.createElement('span');
            count.className = 'surisa-catalog__facet-option-count';
            count.textContent = String(counts[val]);

            label.appendChild(input);
            label.appendChild(box);
            label.appendChild(text);
            label.appendChild(count);
            return label;
          }

          if (!advanced) {
            values.forEach(function (val) { body.appendChild(buildOption(val)); });
            return;
          }

          // ── Avanzado: buscador + chips + lista scrollable en columnas ──
          var search = document.createElement('input');
          search.type = 'search';
          search.className = 'surisa-catalog__facet-search';
          search.setAttribute('data-facet-search', '');
          search.setAttribute('autocomplete', 'off');
          search.placeholder = self.i18nSearchPlaceholder;
          search.setAttribute('aria-label', self.i18nSearchPlaceholder);
          body.appendChild(search);

          var chips = document.createElement('div');
          chips.className = 'surisa-catalog__facet-chips';
          chips.setAttribute('data-facet-chips', '');
          body.appendChild(chips);

          var list = document.createElement('div');
          list.className = 'surisa-catalog__facet-list';
          list.setAttribute('data-facet-list', '');
          values.forEach(function (val) { list.appendChild(buildOption(val)); });
          body.appendChild(list);

          // Buscador: filtra la lista al instante (oculta opciones que no
          // matchean por texto). NO dispara filtrado de la tabla — solo acota
          // las opciones visibles. El filtrado real es al marcar el checkbox.
          search.addEventListener('input', function () {
            var q = search.value.trim().toLowerCase();
            Array.prototype.forEach.call(list.querySelectorAll('[data-facet-option]'), function (opt) {
              var v = (opt.getAttribute('data-value') || '').toLowerCase();
              opt.hidden = q !== '' && v.indexOf(q) === -1;
            });
          });

          this.renderFacetChips(facet);
        }

        /** Chips de los valores seleccionados (modo avanzado), en la cabecera
         *  del popover. Cada chip es eliminable: desmarca su checkbox y dispara
         *  el filtrado inmediato. Se reconstruye en cada cambio de estado vía
         *  syncUiFromState (para reflejar selecciones desde checkbox o URL). */
        renderFacetChips(facet) {
          var chips = facet.querySelector('[data-facet-chips]');
          if (!chips) return;
          var key = facet.getAttribute('data-filter-key');
          var active = (this.state.filters || {})[key] || [];
          var self = this;

          while (chips.firstChild) chips.removeChild(chips.firstChild);
          if (!active.length) { chips.hidden = true; return; }
          chips.hidden = false;

          active.forEach(function (val) {
            var chip = document.createElement('button');
            chip.type = 'button';
            chip.className = 'surisa-catalog__facet-chip';
            chip.setAttribute('aria-label', (self.i18nChipRemoveValue || '__VALUE__').replace('__VALUE__', val));

            var txt = document.createElement('span');
            txt.textContent = val;
            chip.appendChild(txt);

            var x = document.createElement('span');
            x.className = 'surisa-catalog__facet-chip-x';
            x.setAttribute('aria-hidden', 'true');
            x.innerHTML =
              '<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
              'stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M6 6l12 12M6 18 18 6"/></svg>';
            chip.appendChild(x);

            chip.addEventListener('click', function () {
              Array.prototype.forEach.call(
                facet.querySelectorAll('input[type="checkbox"]'),
                function (cb) { if (cb.value === val) cb.checked = false; }
              );
              self.withScrollPreserved(facet, function () { self.dispatchFromDom(facet); });
            });
            chips.appendChild(chip);
          });
        }

        /** Ejecuta `fn` (un dispatch de filtro) preservando el scroll del
         *  listado avanzado y del drawer móvil. Al marcar/quitar una opción se
         *  añade/quita un chip ARRIBA del listado y/o el checkbox recibe foco;
         *  el navegador reajustaba el scroll del drawer hasta el fondo. Capturamos
         *  scrollTop antes y lo restauramos justo después y en el siguiente frame
         *  (tras el reflujo). En desktop el drawer no existe (closest → null) y
         *  el listado conserva su scroll: no-op inofensivo. */
        withScrollPreserved(facet, fn) {
          var listEl   = facet.querySelector('[data-facet-list]');
          var drawerEl = facet.closest('.surisa-catalog__filters-body');
          var lt = listEl ? listEl.scrollTop : 0;
          var dt = drawerEl ? drawerEl.scrollTop : 0;
          fn();
          var restore = function () {
            if (listEl) listEl.scrollTop = lt;
            if (drawerEl) drawerEl.scrollTop = dt;
          };
          restore();
          if (typeof requestAnimationFrame === 'function') requestAnimationFrame(restore);
        }

        /* ── State reconstruction from DOM (single source of truth) ─
         *
         *  CRITICAL: this.facets contains TWO copies of each filter (desktop
         *  pill + mobile drawer facet) sharing the same data-filter-key.
         *  Iterating both and assigning `out[key] = ...` causes the second
         *  iteration to overwrite the first → if the user marks "1.8159" in
         *  the desktop pill but the mobile drawer (hidden) still has only
         *  "1.1248" from the previous sync, the read returns ["1.1248"] and
         *  the new check is lost.
         *
         *  Fix: group facets by key, pick ONE canonical facet per key. If
         *  `sourceFacet` is provided (event-driven dispatch), the canonical
         *  for that key is the source facet (the one the user just touched).
         *  Otherwise, default to the first facet found for that key.
         *  Counterintuitive but stable: the two sets are kept in sync via
         *  syncUiFromState after every state-change, so reading from either
         *  yields the same result outside event-driven flows. */

        readFiltersFromDom(sourceFacet) {
          var out = {};
          var unit = (this.state && this.state.unit) || 'mm';
          var sourceKey = sourceFacet ? sourceFacet.getAttribute('data-filter-key') : null;

          // Group facets by key.
          var byKey = {};
          this.facets.forEach(function (facet) {
            var key = facet.getAttribute('data-filter-key');
            if (!key) return;
            if (!byKey[key]) byKey[key] = [];
            byKey[key].push(facet);
          });

          Object.keys(byKey).forEach(function (key) {
            var facets = byKey[key];
            // Pick canonical facet: source if it matches the key, else first.
            var canonical = facets[0];
            if (sourceKey === key) {
              for (var i = 0; i < facets.length; i++) {
                if (facets[i] === sourceFacet) { canonical = facets[i]; break; }
              }
            }
            var type = canonical.getAttribute('data-filter-type');
            if (type === 'checkbox') {
              var checked = Array.from(canonical.querySelectorAll('input[type="checkbox"]:checked'))
                .map(function (el) { return el.value; });
              if (checked.length > 0) out[key] = checked;
            } else if (type === 'range') {
              var bounds = canonical._mmBounds || {
                min: Number(canonical.getAttribute('data-range-min')),
                max: Number(canonical.getAttribute('data-range-max'))
              };
              var minInput = canonical.querySelector('[data-range-thumb="min"]');
              var maxInput = canonical.querySelector('[data-range-thumb="max"]');
              if (!minInput || !maxInput) return;

              var isDim = isDimensionalMmKey(key);
              var inMode = isDim && unit === 'in';

              // Compute display bounds (canonical mm converted to active unit).
              var dispBoundsMin = inMode ? +(bounds.min * MM_TO_IN).toFixed(2) : bounds.min;
              var dispBoundsMax = inMode ? +(bounds.max * MM_TO_IN).toFixed(2) : bounds.max;
              var mnDisp = Number(minInput.value);
              var mxDisp = Number(maxInput.value);

              // CRITICAL: compare as INTEGERS in hundredths, not as floats.
              // JS floating-point makes `19.69 - 19.64 = 0.05000000000000071`
              // — exactly enough to fail a `> 0.05` epsilon check by ~7e-17.
              // That false-positive was the root of the cascading bug:
              // syncRangeFacetUnit set inputs to display values matching a
              // stale state.filters (e.g. 19.64 from a prior bug), then
              // dispatchFromDom on a SIBLING pill clear re-read and emitted
              // the stale filter every time, never letting state recover.
              //
              // Switching to integer comparison (Math.round * 100) eliminates
              // floating-point noise. Epsilon in hundredths: 5 (= 0.05 in) or
              // 50 (= 0.5 mm) — both well under one step (10 hundredths in
              // unit=in, 100 hundredths in unit=mm) so user moves still
              // trigger narrowing.
              var mnDispI = Math.round(mnDisp * 100);
              var mxDispI = Math.round(mxDisp * 100);
              var dispBoundsMinI = Math.round(dispBoundsMin * 100);
              var dispBoundsMaxI = Math.round(dispBoundsMax * 100);
              var EPS_I = inMode ? 5 : 50;

              var narrowed = Math.abs(mnDispI - dispBoundsMinI) > EPS_I
                          || Math.abs(mxDispI - dispBoundsMaxI) > EPS_I;

              if (narrowed) {
                // Convert display values to canonical mm for state storage.
                var mnMm = inMode ? mnDisp * IN_TO_MM : mnDisp;
                var mxMm = inMode ? mxDisp * IN_TO_MM : mxDisp;
                out[key] = {
                  min: Math.round(mnMm * 100) / 100,
                  max: Math.round(mxMm * 100) / 100
                };
              }
            }
          });
          return out;
        }

        dispatchFromDom(sourceFacet) {
          var filters = this.readFiltersFromDom(sourceFacet);
          this.state.filters = filters;
          this.dispatchEvent(new CustomEvent('surisa-catalog-filters:change', {
            bubbles: true,
            detail: { sectionId: this.sectionId, filters: filters }
          }));
        }

        handleReset() {
          // Wipe all checkboxes + range thumbs (in display unit) + dispatch.
          // Then re-sync range facets so they re-render cleanly.
          // Same display-unit snap rule as clearPillFilter (see comment there):
          // snapping to canonical mm bounds while inputs are in `in` mode
          // causes the browser to clamp and dispatchFromDom emits a stale
          // filter that never clears.
          var self = this;
          this.facets.forEach(function (facet) {
            var type = facet.getAttribute('data-filter-type');
            if (type === 'checkbox') {
              Array.from(facet.querySelectorAll('input[type="checkbox"]')).forEach(function (cb) {
                cb.checked = false;
              });
            } else if (type === 'range') {
              var displayBounds = self.getDisplayBounds(facet);
              var minInput = facet.querySelector('[data-range-thumb="min"]');
              var maxInput = facet.querySelector('[data-range-thumb="max"]');
              if (minInput) minInput.value = String(displayBounds.min);
              if (maxInput) maxInput.value = String(displayBounds.max);
            }
          });
          this.dispatchFromDom();
          var unit = (this.state && this.state.unit) || 'mm';
          this.facets.forEach(function (facet) {
            if (facet.getAttribute('data-filter-type') === 'range') {
              self.syncRangeFacetUnit(facet, unit);
            }
          });
        }

        /* ── Sync from catalog-table ───────────────────────────────── */

        handleCatalogEvent(evt) {
          if (!evt.detail) return;
          if (evt.detail.sectionId && this.sectionId && evt.detail.sectionId !== this.sectionId) return;

          var firstSync = !this.catalogData || !this.catalogData.rows;
          if (evt.detail.rows || evt.detail.filters || evt.detail.columns) {
            this.catalogData = {
              rows:    evt.detail.rows    || (this.catalogData && this.catalogData.rows)    || [],
              filters: evt.detail.filters || (this.catalogData && this.catalogData.filters) || [],
              columns: evt.detail.columns || (this.catalogData && this.catalogData.columns) || []
            };
          }
          if (evt.detail.state) this.state = Object.assign({}, evt.detail.state);

          // Render checkbox facets on first sync, o cuando el dataset crece
          // (datasetExtended: la tabla concatenó las páginas restantes de una
          // colección >250). Reconstruir las opciones asegura que aparezcan los
          // valores que solo existen en filas de páginas posteriores; el
          // syncUiFromState() de abajo re-marca las casillas activas, así que
          // la selección del usuario se conserva.
          if ((firstSync || evt.detail.datasetExtended) && this.catalogData.rows) {
            var self = this;
            this.facets.forEach(function (facet) {
              if (facet.getAttribute('data-filter-type') === 'checkbox') {
                self.renderCheckboxOptions(facet, self.catalogData.rows);
              }
            });
          }

          // Sync UI to state — applies on first sync (URL params loaded) and
          // on state-change events from outside (chip removal, etc.).
          this.syncUiFromState();

          // Update mobile apply button label + active count badge.
          var filteredCount = evt.detail.filteredCount;
          if (filteredCount == null) filteredCount = this.catalogData.rows.length;
          this.updateApplyLabel(filteredCount);
          this.updateActiveCount();
        }

        /** Re-render UI controls to match this.state.filters. Range thumbs +
         *  fills move (in the unit currently active); checkbox `checked` flips.
         *  Range sync delegates to syncRangeFacetUnit which handles the mm↔in
         *  conversion based on state.unit (Opción B).
         *
         *  Also re-renders the desktop pills: value text (label vs concrete
         *  value vs "N seleccionados"), active-state attr, chevron/X visibility,
         *  and the "Restablecer" link inside each popover header. */
        syncUiFromState() {
          var filters = this.state.filters || {};
          var unit = (this.state && this.state.unit) || 'mm';
          var self = this;
          this.facets.forEach(function (facet) {
            var key  = facet.getAttribute('data-filter-key');
            var type = facet.getAttribute('data-filter-type');
            if (type === 'checkbox') {
              var active = filters[key] || [];
              Array.from(facet.querySelectorAll('input[type="checkbox"]')).forEach(function (cb) {
                cb.checked = active.indexOf(cb.value) !== -1;
              });
              // Modo avanzado: re-sincroniza los chips de selección.
              if (facet.querySelector('[data-facet-chips]')) self.renderFacetChips(facet);
            } else if (type === 'range') {
              self.syncRangeFacetUnit(facet, unit);
            }
          });
          // Render desktop pills (label, active state, chevron/X, reset link).
          this.pills.forEach(function (pill) { self.renderPillState(pill); });
          // Clear-all link visibility (hidden if no filter active).
          if (this.clearAllBtn) {
            var hasAnyActive = Object.keys(filters).some(function (k) {
              var v = filters[k];
              return (Array.isArray(v) && v.length > 0)
                  || (v && typeof v === 'object' && (v.min != null || v.max != null));
            });
            this.clearAllBtn.hidden = !hasAnyActive;
          }
        }

        /** Render a single pill based on this.state.filters[key]:
         *   - Inactive: chevron visible, X hidden, default label, reset link hidden.
         *   - Active checkbox (1 value): X visible, value=label_value, reset visible.
         *   - Active checkbox (N values): X visible, value="N seleccionados", reset visible.
         *   - Active range: X visible, value="min–max unit", reset visible. */
        renderPillState(pill) {
          var key  = pill.getAttribute('data-filter-key');
          var type = pill.getAttribute('data-filter-type');
          if (!key || !type) return;
          var filterValue = (this.state.filters || {})[key];
          var pillBtn   = pill.querySelector('[data-pill-toggle]');
          var valueEl   = pill.querySelector('[data-pill-value]');
          var chevronEl = pill.querySelector('[data-pill-chevron]');
          var closeEl   = pill.querySelector('[data-pill-close]');
          var resetEl   = pill.querySelector('[data-pill-reset]');

          var isActive = false;
          var displayLabel = pill._defaultLabel || (valueEl ? valueEl.textContent : '');

          if (type === 'checkbox' && Array.isArray(filterValue) && filterValue.length > 0) {
            isActive = true;
            if (filterValue.length === 1) {
              displayLabel = String(filterValue[0]);
            } else {
              var tpl = filterValue.length === 1 ? this.i18nMultiOne : this.i18nMultiOther;
              displayLabel = tpl
                ? tpl.replace('__N__', String(filterValue.length))
                : filterValue.length + ' seleccionados';
            }
          } else if (type === 'range' && filterValue && typeof filterValue === 'object'
                     && (filterValue.min != null || filterValue.max != null)) {
            isActive = true;
            var unit = (this.state && this.state.unit) || 'mm';
            var isDim = isDimensionalMmKey(key);
            var bounds = pill._mmBounds || {
              min: Number(pill.getAttribute('data-range-min')),
              max: Number(pill.getAttribute('data-range-max'))
            };
            var mn = filterValue.min != null ? filterValue.min : bounds.min;
            var mx = filterValue.max != null ? filterValue.max : bounds.max;
            if (isDim && unit === 'in') {
              mn = +(mn * MM_TO_IN).toFixed(2);
              mx = +(mx * MM_TO_IN).toFixed(2);
            }
            var unitLabel = isDim ? ' ' + unit : '';
            displayLabel = formatRangeValue(mn, isDim ? unit : 'mm') + '–' + formatRangeValue(mx, isDim ? unit : 'mm') + unitLabel;
          }

          if (valueEl) valueEl.textContent = displayLabel;
          if (pillBtn) {
            pillBtn.setAttribute('data-active', isActive ? 'true' : 'false');
          }
          if (chevronEl) chevronEl.hidden = isActive;
          if (closeEl) closeEl.hidden = !isActive;
          if (resetEl) resetEl.hidden = !isActive;
        }

        updateApplyLabel(filteredCount) {
          if (!this.applyLabel) return;
          var tpl = filteredCount === 1 ? this.i18nApplyOne : this.i18nApplyOther;
          if (!tpl) return;
          this.applyLabel.textContent = tpl.replace('__N__', String(filteredCount));
        }

        updateActiveCount() {
          // Mobile filter trigger button shows a badge with the number of
          // active filters (single concept: each checkbox value = 1, each
          // range filter = 1). Desktop pill bar shows the same info via the
          // pill active states + clear-all link (handled in syncUiFromState).
          var filters = this.state.filters || {};
          var total = 0;
          Object.keys(filters).forEach(function (k) {
            var v = filters[k];
            if (Array.isArray(v)) total += v.length;
            else if (v && typeof v === 'object') total += 1;
          });
          if (this.activeBadge) {
            if (total > 0) {
              this.activeBadge.textContent = String(total);
              this.activeBadge.hidden = false;
            } else {
              this.activeBadge.hidden = true;
            }
          }

          // v0.8.0 mobile pass: also drive the right-side status copy on the
          // Filters CTA button (data-filters-status) + the border-color flag
          // (data-has-filters). Templates come from the button's data-i18n-*
          // attrs (see section liquid).
          if (this.triggerBtn) {
            this.triggerBtn.setAttribute('data-has-filters', total > 0 ? 'true' : 'false');
            var statusEl = this.triggerBtn.querySelector('[data-filters-status]');
            if (statusEl) {
              var copy;
              if (total === 0) {
                copy = this.triggerBtn.getAttribute('data-i18n-filters-none') || '';
              } else if (total === 1) {
                copy = this.triggerBtn.getAttribute('data-i18n-filters-applied-one') || '';
              } else {
                var tpl = this.triggerBtn.getAttribute('data-i18n-filters-applied-other') || '';
                copy = tpl.replace('__N__', String(total));
              }
              statusEl.textContent = copy;
            }
          }
        }
      }
    );
  }

  /* ── Bridge: <surisa-catalog-filters> → <surisa-catalog-table>.setState ── */

  document.addEventListener('surisa-catalog-filters:change', function (evt) {
    if (!evt.detail) return;
    var sectionId = evt.detail.sectionId || '';
    var table = sectionId
      ? document.querySelector('surisa-catalog-table[data-section-id="' + cssEscape(sectionId) + '"]')
      : document.querySelector('surisa-catalog-table');
    if (!table) return;
    table.setState({ filters: evt.detail.filters || {} });
  });

  /* ── <surisa-techhero-carousel> ──────────────────────────────────── */

  if (!customElements.get('surisa-techhero-carousel')) {
    customElements.define(
      'surisa-techhero-carousel',
      class SurisaTechheroCarousel extends HTMLElement {
        connectedCallback() {
          this.images = this.querySelectorAll('.surisa-catalog__techhero-image');
          this.thumbs = this.querySelectorAll('.surisa-catalog__techhero-thumb');
          this.total = this.images.length;
          this.currentIndex = 0;
          // Hover-preview solo en desktop con puntero fino (ratón); en táctil
          // no hay hover y el click sigue fijando la imagen.
          this.hoverCapable = !!(window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches);

          var self = this;

          // "Ampliar" → visor compartido <surisa-image-lightbox> (Fix 9). Se
          // cablea SIEMPRE (también con 1 sola imagen), antes del early-return
          // de thumbs/navegación de abajo.
          var scope = this.closest('.surisa-catalog__techhero') || document;
          this.lightbox = scope.querySelector('surisa-image-lightbox');
          scope.querySelectorAll('[data-techhero-lightbox-open]').forEach(function (btn) {
            btn.addEventListener('click', function (evt) {
              evt.preventDefault();
              self.openLightbox();
            });
          });

          if (this.total <= 1) return;

          this.thumbs.forEach(function (thumb) {
            var target = parseInt(thumb.getAttribute('data-target'), 10);
            // Click = fija la imagen (persiste). Hover = previsualiza sin fijar.
            thumb.addEventListener('click', function (evt) {
              evt.preventDefault();
              if (!isNaN(target)) self.show(target);
            });
            if (self.hoverCapable) thumb.addEventListener('mouseenter', function () {
              if (!isNaN(target)) self._display(target);
            });
          });

          if (this.hoverCapable) {
            var thumbsWrap = this.querySelector('.surisa-catalog__techhero-thumbs');
            if (thumbsWrap) thumbsWrap.addEventListener('mouseleave', function () { self._display(self.currentIndex); });
          }

          this.show(0);
        }

        openLightbox() {
          if (!this.lightbox || typeof this.lightbox.open !== 'function') return;
          var images = Array.prototype.slice.call(this.images).map(function (im) {
            return { src: im.getAttribute('src') || '', alt: im.getAttribute('alt') || '' };
          });
          if (!images.length) return;
          var self = this;
          // Al cerrar, sincroniza el carrusel con la última imagen vista.
          this.lightbox.onClose = function (idx) {
            if (self.total > 1 && typeof self.show === 'function') self.show(idx);
          };
          this.lightbox.open(images, this.currentIndex || 0);
        }

        // Fija la imagen idx (commit): persiste como imagen por defecto.
        show(idx) {
          this.currentIndex = idx;
          this._display(idx);
        }

        // Muestra visualmente la imagen idx SIN fijarla (hover-preview); al
        // salir de los thumbs se revierte a this.currentIndex.
        _display(idx) {
          this.images.forEach(function (img, i) { img.hidden = i !== idx; });
          this.thumbs.forEach(function (thumb, i) {
            var active = i === idx;
            thumb.classList.toggle('is-active', active);
            thumb.setAttribute('aria-selected', active ? 'true' : 'false');
          });
        }
      }
    );
  }
})();
